/**
 * Brick & Board — "Upgrade your deck" (Magic: The Gathering)
 * No AI, no paid services. Data sources:
 *   - EDHREC public JSON (json.edhrec.com) for Commander popularity/synergy
 *   - Scryfall (api.scryfall.com) for legality, color identity, popularity rank
 *   - This store's storefront (search/suggest.json + /products/{handle}.js) for live stock & prices
 * Picks best-ranked in-stock cards that fit the budget, cheapest acceptable variant first.
 * Commander brackets (WotC, Oct 2025 / Feb 2026 rules): Game Changers come live from Scryfall
 * (is:gamechanger); two-card infinite combos, mass land denial and extra-turn cards come from a
 * Commander Spellbook snapshot shipped as assets/bb-two-card-combos.json (its API is not open to
 * other sites' browsers). This is the same data EDHREC, Moxfield and Archidekt use for their checks.
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-bb-deck-upgrade]';
  var SCRYFALL = 'https://api.scryfall.com';
  var EDHREC = 'https://json.edhrec.com/pages/commanders/';
  var SCRYFALL_GAP_MS = 120; // ~8 req/s, under Scryfall's 10/s general limit
  // Scryfall's hard limit for /cards/search, /cards/named and /cards/collection is 2 per second
  // (a 429 locks the visitor out for 30s), so those calls are spaced at least 550ms apart.
  var SCRYFALL_SLOW_GAP_MS = 550;
  var SCRYFALL_SLOW_RE = /^\/cards\/(?:search|named|collection|random)/;
  var STORE_CONCURRENCY = 5;
  var CHECK_CHUNK = 10;
  var MAX_CHECKS = 300; // big budgets need a deeper look than the top 150
  var CAND_POOL = 300;
  // Auto picks: no count limit. Every in-stock, in-filter upgrade that clearly beats the card it would
  // replace goes in while the budget lasts; the quality bar (not a count) keeps small budgets to a few strong picks.
  var AUTO_CEILING = 99; // safety ceiling only (a whole Commander deck); stock lookups are capped by MAX_CHECKS
  var MIN_SCORE = 0.25;  // EDHREC score (inclusion + 0.6 × synergy) a pick must reach
  var MIN_GAIN = 0.15;   // …and how much it must beat the weakest card it would replace
  /* bb-du roles v1: role chips (filter) + sort over the upgrade list. Roles = Scryfall Tagger index (bb-card-roles.json,
     tools/build_roles.py in /workspace/du-roles) + this file's cardRole() heuristic + Game Changer flag + type line (lands). */
  var ROLE_CHIPS = [['ramp', 'Ramp'], ['draw', 'Card draw'], ['removal', 'Removal'], ['wipe', 'Board wipe'], ['counter', 'Counterspell'],
    ['tutor', 'Tutor'], ['threat', 'Threat'], ['protection', 'Protection'], ['gc', 'Game Changer'], ['land', 'Lands']];
  var HEUR_ROLE = { 'Mana ramp': 'ramp', 'Card draw': 'draw', 'Removal': 'removal', 'Board wipe': 'wipe', 'Counterspell': 'counter', 'Tutor': 'tutor',
    'Protection': 'protection', 'Threat': 'threat', 'Cheap threat': 'threat', 'Planeswalker': 'threat' };
  var roleIdxP = null;
  function loadRoleIndex(url) {
    if (roleIdxP) return roleIdxP;
    roleIdxP = (url ? fetchJson(url, { credentials: 'omit', timeout: 20000 }) : Promise.reject(new Error('no url'))).then(function (d) {
      var map = Object.create(null);
      if (d && d.names && d.bits && d.tags) d.names.forEach(function (n, i) { map[n] = d.bits[i]; });
      return { map: map, tags: (d && d.tags) || [] };
    }).catch(function () { return null; });
    return roleIdxP;
  }
  function rolesOf(c, idx) {
    var out = Object.create(null);
    var b = idx && idx.map[c.key];
    if (b) idx.tags.forEach(function (t, i) { if (b & (1 << i)) out[t] = true; });
    var h = HEUR_ROLE[c.role]; if (h) out[h] = true;
    (c.upTags || []).forEach(function (t) { if (t === 'Tutor') out.tutor = true; else if (t === 'Fast mana') out.ramp = true; });
    if (c.isGC) out.gc = true;
    if (/\bLand\b/.test(String((c.card && c.card.type_line) || '').split('//')[0])) { out.land = true; delete out.tutor; } // fetch lands are tagged "tutor" on Scryfall; shoppers mean spell tutors
    return out;
  }
  var MORE_MAX = 18, MORE_SHOWN = 6, MORE_CHUNKS = 3; // "More upgrades worth a look": strong cards that didn't fit
  var MAX_OUTSIDE = 6;
  var MAX_PRINTINGS = 4;
  var FORMAT_LABEL = { commander: 'Commander', standard: 'Standard', pioneer: 'Pioneer', modern: 'Modern', pauper: 'Pauper' };
  var COND_LABEL = { NM: 'Near Mint', LP: 'Lightly Played', MP: 'Moderately Played', HP: 'Heavily Played', DMG: 'Damaged' };
  var COND_ORDER = { NM: 0, LP: 1, MP: 2, HP: 3, DMG: 4 };
  var CONDITIONS = [
    { id: 'NM', re: /^(near\s*mint|nm)\b/i },
    { id: 'LP', re: /^(lightly\s*played|lp|excellent)\b/i },
    { id: 'MP', re: /^(moderately\s*played|mp|played)\b/i },
    { id: 'HP', re: /^(heavily\s*played|hp|poor)\b/i },
    { id: 'DMG', re: /^(damaged|dmg)\b/i }
  ];
  var HEADER_RE = /^(?:\/\/\s*)?(?:\(\d+\)\s*)?(commanders?|deck|main|mainboard|main\s*deck|sideboard|side\s*board|maybeboard|maybe\s*board|considering|companion|tokens?|lands?|creatures?|instants?|sorceries?|sorcerys?|artifacts?|enchantments?|planeswalkers?|battles?|other)\s*(?:\(\d+\))?\s*:?$/i;
  var SKIP_HEADER_RE = /^(maybeboard|maybe\s*board|considering|tokens?)$/i;
  var BASIC_RE = /^(snow-covered\s+)?(plains|island|swamp|mountain|forest|wastes)$/i;
  var LANGUAGE_RE = /\((japanese|korean|chinese|simplified chinese|traditional chinese|russian|german|french|italian|spanish|portuguese|phyrexian)\)|\bjapanese\b/i;
  var SET_CODE_RE = /^[A-Za-z0-9]{2,6}-\d{1,4}[A-Za-z]?$/;
  var COLLECTOR_ONLY_RE = /^\d{1,5}[A-Za-z]?$/;
  var TREATMENT_RE =
    /^(borderless|extended\s*art|showcase|retro\s*frame|serialized|etched|foil|non[\s-]*foil|full\s*art|textless|promo|alternate\s*art|universes\s*beyond|oil\s*slick|step[\s-]*and[\s-]*compleat|surge\s*foil|confetti\s*foil|rainbow\s*foil|gilded|raised\s*foil|fracture\s*foil|neon\s*ink|phyrexian|japanese|korean|chinese|russian|german|french|italian|spanish|portuguese|pool\s*party\s*foil|comic[\s-]*book|anime|stamped|prerelease|bundle|buy[\s-]*a[\s-]*box|fnm|judge|sdcc).*$/i;

  /* ---------------- helpers ---------------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function ws(s) { return String(s || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim(); }
  function stripDiacritics(s) { try { return String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (e) { return String(s); } }
  function nameKey(s) {
    return stripDiacritics(ws(s)).toLowerCase().replace(/[‘’`´]/g, "'").replace(/\s*\/\/?\s*/g, ' // ').replace(/\s+/g, ' ').trim();
  }
  function frontKey(s) { return nameKey(s).split(' // ')[0]; }
  function cartUrl() {
    var u = rootPath() + 'cart';
    try {
      var pid = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (pid && /^\d+$/.test(pid)) u += '?preview_theme_id=' + pid;
    } catch (e) {}
    return u;
  }
  function rootPath() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(n);
    } catch (e) { return '$' + n.toFixed(2); }
  }
  function pct(x) { return Math.round(x * 100) + '%'; }

  async function fetchJson(url, opts, attempt) {
    attempt = attempt || 0;
    opts = opts || {};
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, opts.timeout || 20000) : null;
    var res;
    try {
      res = await fetch(url, {
        method: opts.method || 'GET',
        credentials: opts.credentials || 'omit',
        headers: opts.headers || { Accept: 'application/json' },
        body: opts.body,
        signal: ctrl ? ctrl.signal : undefined
      });
    } catch (e) {
      if (timer) clearTimeout(timer);
      if (attempt < 2) { await sleep(800 * (attempt + 1)); return fetchJson(url, opts, attempt + 1); }
      throw e;
    }
    if (timer) clearTimeout(timer);
    if ((res.status === 429 || res.status >= 500) && attempt < 3) {
      var wait = parseInt(res.headers.get('Retry-After') || '', 10);
      await sleep((isFinite(wait) ? wait : 1 + attempt) * 1000);
      return fetchJson(url, opts, attempt + 1);
    }
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('HTTP ' + res.status);
    var text = await res.text();
    try { return JSON.parse(text); } catch (e) { return null; }
  }

  /* Scryfall: serialised queue with a minimum gap between requests. */
  var sfChain = Promise.resolve();
  var sfLast = 0;
  function scryfall(path, body) {
    var run = sfChain.then(async function () {
      var wait = sfLast + (SCRYFALL_SLOW_RE.test(path) ? SCRYFALL_SLOW_GAP_MS : SCRYFALL_GAP_MS) - Date.now();
      if (wait > 0) await sleep(wait);
      sfLast = Date.now();
      return fetchJson(SCRYFALL + path, body ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      } : null);
    });
    sfChain = run.catch(function () {});
    return run;
  }

  async function scryfallCollection(names, opts) {
    var out = Object.create(null);
    var uniq = [];
    var seen = Object.create(null);
    names.forEach(function (n) { var k = frontKey(n); if (!seen[k]) { seen[k] = 1; uniq.push(n); } });
    for (var i = 0; i < uniq.length; i += 75) {
      var chunk = uniq.slice(i, i + 75).map(function (n) { return { name: n.split(/\s*\/\/\s*/)[0] }; });
      var data = await scryfall('/cards/collection', { identifiers: chunk });
      ((data && data.data) || []).forEach(function (c) {
        out[frontKey(c.name)] = c;
        out[nameKey(c.name)] = c;
      });
    }
    // Lines Scryfall's batch lookup missed: alternate/flavor names (e.g. FINAL FANTASY: Through the Ages
    // "Stay with Me" = Rhystic Study, "Endwalker" = Brainstorm) and small typos. /cards/named resolves both.
    var aliases = [];
    var missing = (opts && opts.resolve === false) ? [] : uniq.filter(function (n) { return !out[frontKey(n)] && !out[nameKey(n)] && !BASIC_RE.test(n) && /[a-z].*[a-z]/i.test(n) && !/^(?:https?:|www\.)/i.test(n) && n.length <= 80 && !/\s[—|]\s/.test(n); }).slice(0, 20);
    for (var j = 0; j < missing.length; j++) {
      var n = missing[j].split(/\s*\/\/\s*/)[0];
      var card = await scryfall('/cards/named?exact=' + encodeURIComponent(n)).catch(function () { return null; });
      if (!card || card.object !== 'card') card = await scryfall('/cards/named?fuzzy=' + encodeURIComponent(n)).catch(function () { return null; });
      if (!card || card.object !== 'card') continue;
      out[frontKey(missing[j])] = card;
      out[nameKey(missing[j])] = card;
      if (!out[frontKey(card.name)]) { out[frontKey(card.name)] = card; out[nameKey(card.name)] = card; }
      if (frontKey(card.name) !== frontKey(missing[j])) aliases.push({ from: missing[j], to: card.name });
    }
    Object.defineProperty(out, '_aliases', { value: aliases, enumerable: false });
    return out;
  }

  async function scryfallSearch(q, pages) {
    var cards = [];
    var url = '/cards/search?order=edhrec&unique=cards&q=' + encodeURIComponent(q);
    for (var p = 0; p < (pages || 1) && url; p++) {
      var data = await scryfall(url);
      if (!data || !data.data) break;
      cards = cards.concat(data.data);
      url = data.has_more && data.next_page ? data.next_page.replace(SCRYFALL, '') : null;
    }
    return cards;
  }

  function cardImage(c) {
    if (!c) return null;
    if (c.image_uris) return c.image_uris.small || c.image_uris.normal;
    if (c.card_faces && c.card_faces[0] && c.card_faces[0].image_uris) return c.card_faces[0].image_uris.small;
    return null;
  }
  function isLandCard(c) { return !!c && /\bLand\b/.test(String(c.type_line || '').split('//')[0]); }
  function canBeCommander(c) {
    if (!c) return false;
    var t = String(c.type_line || '');
    var front = String(t).split('//')[0];
    if (c.legalities && c.legalities.commander === 'banned') return false;
    return (/Legendary/.test(front) && /Creature/.test(front)) || (/Legendary/.test(front) && /Background/.test(front)) ||
      /can be your commander/i.test(c.oracle_text || (c.card_faces && c.card_faces[0] && c.card_faces[0].oracle_text) || '');
  }
  function canPartner(c) { return /\bpartner\b|choose a background|friends forever|doctor's companion/i.test(oracleOf(c)) || /Background/.test(String(c.type_line || '')); }
  function unionIdentity(cards) {
    var out = [];
    cards.forEach(function (c) { (c && c.color_identity || []).forEach(function (x) { if (out.indexOf(x) === -1) out.push(x); }); });
    return out;
  }
  function covers(id, need) { return need.every(function (x) { return id.indexOf(x) !== -1; }); }

  /* Work out the commander(s) from a parsed list.
     Order: explicit header/tag (Commander, //Commander, *CMDR*, [Commander], "(1) Commander") >
     a 1–2 line block at the bottom (Moxfield plain text / MTGO) > a 1–2 line block at the top >
     best guess among legendary cards (prefers one whose colors cover the whole deck). */
  function detectCommanders(entries, cardOf) {
    var legends = [], seenL = Object.create(null);
    entries.forEach(function (e) {
      var c = cardOf(e);
      if (c && canBeCommander(c) && e.section !== 'sideboard' && !seenL[c.name]) { seenL[c.name] = 1; legends.push(c); }
    });
    var res = { cards: [], how: '', candidates: legends };
    function uniqCards(list) { var o = []; list.forEach(function (e) { var c = cardOf(e); if (c && o.indexOf(c) === -1) o.push(c); }); return o; }
    var hdr = uniqCards(entries.filter(function (e) { return e.section === 'commander' || e.section === 'commanders'; }));
    if (hdr.length) { res.cards = hdr; res.how = 'header'; return res; }
    var blocks = [];
    entries.forEach(function (e) { (blocks[e.block] = blocks[e.block] || []).push(e); });
    blocks = blocks.filter(Boolean);
    function blockOk(b) {
      if (!b || b.length < 1 || b.length > 2) return null;
      if (!b.every(function (e) { return e.qty === 1 && e.section !== 'sideboard' && e.section !== 'companion'; })) return null;
      var cards = b.map(cardOf);
      if (!cards.every(function (c) { return c && canBeCommander(c); })) return null;
      if (cards.length === 2 && !cards.every(canPartner)) return null;
      return cards;
    }
    if (blocks.length >= 2) {
      var bottom = blockOk(blocks[blocks.length - 1]);
      if (bottom) { res.cards = bottom; res.how = 'bottom'; return res; }
      var top = blockOk(blocks[0]);
      if (top) { res.cards = top; res.how = 'top'; return res; }
    }
    if (!legends.length) return res;
    // Guess: the deck's full color identity (ignoring the legends themselves) should fit inside the commander's.
    var need = unionIdentity(entries.map(cardOf).filter(function (c) { return c && legends.indexOf(c) === -1; }));
    var cover = legends.filter(function (c) { return covers(c.color_identity || [], need); });
    if (cover.length) {
      cover.sort(function (a, b) { return (a.color_identity || []).length - (b.color_identity || []).length || (a.edhrec_rank || 1e9) - (b.edhrec_rank || 1e9); });
      // Tie-break: Moxfield/Archidekt exports put the commander last (or first) even without a blank line.
      var minC = (cover[0].color_identity || []).length;
      var ends = [entries[entries.length - 1], entries[0]].map(function (e) { return e && cardOf(e); });
      var endPick = ends.filter(function (c) { return c && cover.indexOf(c) !== -1 && (c.color_identity || []).length === minC; })[0];
      res.cards = [endPick || cover[0]];
      // A partner legend that would be off-color next to the pick (e.g. Tymna in a Kraum list) is almost
      // certainly the second commander, so guess the pair.
      var pick = res.cards[0];
      if (canPartner(pick)) {
        var mate = legends.filter(function (c) { return c !== pick && canPartner(c) && !covers(pick.color_identity || [], c.color_identity || []); })[0];
        if (mate) res.cards = [pick, mate];
      }
    } else {
      var partners = legends.filter(canPartner), pair = null;
      for (var i = 0; i < partners.length && !pair; i++) for (var j = i + 1; j < partners.length && !pair; j++) {
        if (covers(unionIdentity([partners[i], partners[j]]), need)) pair = [partners[i], partners[j]];
      }
      if (pair) res.cards = pair;
      else res.cards = [legends.slice().sort(function (a, b) { return (b.color_identity || []).length - (a.color_identity || []).length; })[0]];
    }
    res.how = 'guess';
    return res;
  }
  function identityOk(c, colors) {
    return (c.color_identity || []).every(function (x) { return colors.indexOf(x) !== -1; });
  }
  function legalIn(c, fmt) { var l = c && c.legalities && c.legalities[fmt]; return l === 'legal' || (l === 'restricted' && fmt === 'vintage'); }
  function oracleOf(c) {
    if (!c) return '';
    var t = c.oracle_text || '';
    if (c.card_faces) t += '\n' + c.card_faces.map(function (f) { return f.oracle_text || ''; }).join('\n');
    return t.toLowerCase();
  }
  var COLOR_NAME = { W: 'white', U: 'blue', B: 'black', R: 'red', G: 'green' };
  /* Plain-English role for a card, from its Scryfall type line and rules text. */
  function cardRole(c, colorStr) {
    if (!c) return '';
    var t = String(c.type_line || '').split('//')[0];
    var o = oracleOf(c);
    if (/\bLand\b/.test(t)) {
      var mine = (c.produced_mana || []).filter(function (x) { return colorStr.indexOf(x) !== -1; });
      if (mine.length >= 3) return 'Mana fixing land';
      if (mine.length === 2) return 'Dual land for your colors';
      if (/search your library for an? .*land/.test(o)) return 'Mana fixing land';
      if (/(creature|counter|draw|destroy|exile|token|damage)/.test(o)) return 'Utility land';
      return 'Land';
    }
    if (/counter target (spell|activated|noncreature|creature|instant|sorcery|artifact|enchantment)/.test(o)) return 'Counterspell';
    if (/(destroy|exile) (all|each) (creature|nonland|other|artifact|nontoken)|all creatures get -|each creature gets -|deals? \d+ damage to each creature/.test(o)) return 'Board wipe';
    if (/(destroy|exile) (target|up to one target|another target)|deals? (\d+|x|that much) damage to (any target|target|each opponent|up to)|target creature (an opponent controls )?gets -|fights? (target|up to)|return (target|up to one target) [^.]*?(creature|permanent)[^.]* to (its|their) owner|target player sacrifices|each opponent sacrifices/.test(o)) return 'Removal';
    if (/search your library for (a|an|up to (one|two|three)) (basic )?(land|forest|plains|island|swamp|mountain)|put (a|up to \w+) land cards? .*onto the battlefield/.test(o) ||
      (!/\bLand\b/.test(t) && /\badd (\{|one mana|two mana|three mana|x mana|an amount|mana)/.test(o))) return 'Mana ramp';
    if (/draws? (a|an|two|three|four|x|that many|cards equal)\b.*cards?|draw cards equal|investigate|connives?|look at the top .* put .* into your hand/.test(o)) return 'Card draw';
    if (/search your library for (a|an|up to \w+) (card|creature|artifact|enchantment|instant|sorcery)/.test(o)) return 'Tutor';
    if (/discard|hand.*reveal|reveals? (his|her|their) hand/.test(o) && /target (player|opponent)/.test(o)) return 'Hand disruption';
    if (/return target .* from your graveyard|return .* card from your graveyard/.test(o)) return 'Recursion';
    if (/create .*token/.test(o)) return 'Token maker';
    if (/\+1\/\+1 counter|proliferate/.test(o)) return 'Counters synergy';
    if (/creatures you control get \+|other .* you control get \+/.test(o)) return 'Anthem';
    if (/hexproof|indestructible|protection from/.test(o) && /(target|creatures you control)/.test(o) && !/Creature/.test(t)) return 'Protection';
    if (/gain \d+ life|you gain life/.test(o)) return 'Life gain';
    if (/(Instant|Sorcery)/.test(t) && /target creature[^.]*gets \+|double target creature|target creature gains/.test(o)) return 'Combat trick';
    if (/Creature/.test(t)) return (c.cmc || 0) <= 2 ? 'Cheap threat' : 'Threat';
    if (/Planeswalker/.test(t)) return 'Planeswalker';
    if (/Artifact/.test(t) && /Equipment/.test(t)) return 'Equipment';
    return '';
  }

  function edhrecSlug(name) {
    return stripDiacritics(String(name).split(/\s*\/\/\s*/)[0]).toLowerCase()
      .replace(/[’'",.!?:]/g, '').replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* ---------------- deck parsing ---------------- */
  function parseDeck(text) {
    var lines = String(text || '').split(/\r?\n/);
    var section = 'deck';
    var out = [];
    var block = 0, blockHas = false, secBlock = -1;
    lines.forEach(function (raw) {
      var s = ws(raw);
      if (!s) { if (blockHas) { block++; blockHas = false; } return; } // blank line (or just spaces) ends a block
      if (/^(#|\/\/)/.test(s)) return; // comments
      if (/\[maybe(?:board)?\b|\{nodeck\}/i.test(s) && !/\[sideboard/i.test(s)) return; // Archidekt maybeboard / not-in-deck
      var h = s.match(HEADER_RE);
      if (h && !/^\d/.test(s)) { section = h[1].toLowerCase().replace(/\s+/g, ''); if (blockHas) { block++; blockHas = false; } secBlock = block; return; }
      var isCmdr = /\[commander|\*cmdr\*|\(commander\)\s*$/i.test(s);
      var m = s.match(/^(?:SB:\s*)?(\d+)\s*[xX×]?\s+(.+)$/);
      var qty = 1, rest = s;
      if (m) { qty = parseInt(m[1], 10); rest = m[2]; }
      var lineSec = /^SB:/i.test(s) || /\[sideboard/i.test(s) ? 'sideboard' : null; // MTGO "SB:" marks just that line
      rest = rest
        .replace(/\s*\[[^\]]*\]\s*/g, ' ')         // Archidekt [Category] tags
        .replace(/\s*\^[^^]*\^\s*/g, ' ')          // Archidekt ^color tags^
        .replace(/\s*\*[A-Za-z]+\*\s*/g, ' ')      // *F*, *CMDR*, *E*
        .replace(/\s*\((?:foil|etched|commander)\)\s*$/i, '');
      rest = ws(rest)
        .replace(/\s+\([A-Za-z0-9]{2,6}\)\s+[A-Za-z0-9\-★]+\s*$/, '') // (SET) 123
        .replace(/\s+\([A-Za-z0-9]{2,6}\)\s*$/, '');
      rest = ws(rest.replace(/^"|"$/g, ''));
      if (rest.indexOf('//') === -1) rest = rest.replace(/^([^/]+?)\s*\/\s*([^/]+)$/, '$1 // $2'); // "Fire/Ice" → "Fire // Ice"
      if (!rest || HEADER_RE.test(rest)) return;
      if (SKIP_HEADER_RE.test(section)) return;
      out.push({ name: rest, qty: qty || 1, section: isCmdr ? 'commander' : (lineSec || section), block: block, hdr: block === secBlock });
      blockHas = true;
    });
    // Moxfield puts the commander alone at the very bottom, after a blank line, even below "SIDEBOARD:".
    var lastB = out.length ? out[out.length - 1].block : -1;
    var tail = out.filter(function (e) { return e.block === lastB; });
    if (tail.length && tail.length <= 2 && tail.every(function (e) { return e.section === 'sideboard' && !e.hdr; })) tail.forEach(function (e) { e.section = 'deck'; });
    out.forEach(function (e) { delete e.hdr; });
    return out;
  }

  /* ---------------- store (Shopify storefront) ---------------- */
  function conditionId(variantTitle) {
    var t = ws(variantTitle);
    for (var i = 0; i < CONDITIONS.length; i++) if (CONDITIONS[i].re.test(t)) return CONDITIONS[i].id;
    return null;
  }
  function cardNameFromTitle(title) {
    var t = ws(title);
    var dash = t.lastIndexOf(' - ');
    var left = dash >= 0 ? t.slice(0, dash) : t;
    for (;;) {
      var m = left.match(/^(.*)\s*\(([^)]+)\)\s*$/);
      if (!m) break;
      var inner = ws(m[2]);
      if (SET_CODE_RE.test(inner) || COLLECTOR_ONLY_RE.test(inner) || TREATMENT_RE.test(inner) || /foil/i.test(inner)) left = ws(m[1]);
      else break;
    }
    return ws(left);
  }
  function titleMeta(title) {
    var t = ws(title);
    var dash = t.lastIndexOf(' - ');
    var setName = dash >= 0 ? ws(t.slice(dash + 3).replace(/\s+foil$/i, '').replace(/\s*\([^)]*\)/g, '').replace(/\s+\/\s+.*$/, '').replace(/:\s*$/, '')) : '';
    var code = t.match(/\(([A-Za-z0-9]{2,6})-(\d{1,4}[A-Za-z]?)\)/);
    var treat = [];
    var re = /\(([^)]+)\)/g, m;
    while ((m = re.exec(t))) {
      var inner = ws(m[1]);
      if (TREATMENT_RE.test(inner) && !/^foil$/i.test(inner) && treat.indexOf(inner) === -1) treat.push(inner);
    }
    return { setName: setName, setCode: code ? code[1].toUpperCase() : '', collector: code ? code[2] : '', treatment: treat.join(', ') };
  }
  function tagStr(p) { return (Array.isArray(p.tags) ? p.tags.join(',') : String(p.tags || '')).toLowerCase(); }
  function isMtgSingle(p) {
    var type = String(p.type || p.product_type || '').toLowerCase();
    var tags = tagStr(p);
    if (/pokemon|lorcana|one piece|star wars|gundam/.test(type)) return false;
    if (/mtg\s*single|magic.+single/.test(type)) return true;
    return /brand:\s*magic/.test(tags) && /type:\s*single/.test(tags);
  }
  function isFoilProduct(p) {
    var tags = tagStr(p);
    if (/printing:\s*non[\s-]*foil/.test(tags)) return false;
    if (/printing:\s*(foil|etched)/.test(tags)) return true;
    return /foil|etched/i.test(p.title || '') && !/non[\s-]*foil/i.test(p.title || '');
  }
  function sizedImage(url, w) {
    if (!url) return null;
    if (/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
    return url;
  }

  var stockCache = Object.create(null);
  async function storeOffers(cardName, altNames) {
    var key = frontKey(cardName);
    if (stockCache[key]) return stockCache[key];
    var p = (async function () {
      var q = cardName.split(/\s*\/\/\s*/)[0];
      var url = rootPath() + 'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=10' +
        '&resources[options][unavailable_products]=hide&resources[options][fields]=title';
      var data = await fetchJson(url, { credentials: 'same-origin' });
      var prods = ((data && data.resources && data.resources.results && data.resources.results.products) || []).filter(function (pr) {
        if (!pr.available || !isMtgSingle(pr)) return false;
        if (LANGUAGE_RE.test(pr.title || '')) return false;
        var full = cardNameFromTitle(pr.title);
        // Alternate-name printings are titled "Flavor Name - Card Name", so check each side too.
        var names = [full].concat(full.indexOf(' - ') >= 0 ? full.split(' - ') : []);
        var want = [nameKey(cardName)].concat((altNames || []).map(nameKey));
        return names.some(function (x) {
          var nk = nameKey(x);
          return want.indexOf(nk) !== -1 || nk.split(' // ')[0] === key;
        });
      });
      // Cheapest printings first; keep a mix of non-foil and foil.
      prods.sort(function (a, b) { return parseFloat(a.price_min || a.price) - parseFloat(b.price_min || b.price); });
      var nf = prods.filter(function (x) { return !isFoilProduct(x); }).slice(0, MAX_PRINTINGS - 1);
      var fo = prods.filter(isFoilProduct).slice(0, 2);
      var chosen = nf.concat(fo).slice(0, MAX_PRINTINGS + 1);
      var offers = [];
      for (var i = 0; i < chosen.length; i++) {
        var pr = chosen[i];
        var full = await fetchJson(rootPath() + 'products/' + encodeURIComponent(pr.handle) + '.js', { credentials: 'same-origin' });
        if (!full || !full.variants) continue;
        var meta = titleMeta(full.title);
        var foil = isFoilProduct(full);
        var img = (full.featured_image) || pr.image || (pr.featured_image && pr.featured_image.url);
        full.variants.forEach(function (v) {
          if (!v.available) return;
          var cond = conditionId(v.title || v.option1 || '');
          if (!cond) return;
          offers.push({
            variantId: v.id, cond: cond, foil: foil, price: Number(v.price), productTitle: full.title,
            handle: full.handle, url: (full.url || ('/products/' + full.handle)).split('?')[0],
            setName: meta.setName, setCode: meta.setCode, collector: meta.collector, treatment: meta.treatment,
            image: sizedImage(img && img.indexOf('//') === 0 ? 'https:' + img : img, 200)
          });
        });
      }
      offers.sort(function (a, b) { return a.price - b.price || COND_ORDER[a.cond] - COND_ORDER[b.cond]; });
      return offers;
    })();
    stockCache[key] = p;
    p.catch(function () { delete stockCache[key]; });
    return p;
  }

  async function mapPool(items, limit, fn) {
    var i = 0;
    async function worker() { while (i < items.length) { var idx = i++; await fn(items[idx], idx); } }
    var ws_ = [];
    for (var w = 0; w < Math.min(limit, items.length); w++) ws_.push(worker());
    await Promise.all(ws_);
  }

  async function postCartAdd(items) {
    var res = await fetch(rootPath() + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    });
    var body = await res.text();
    var msg = '';
    try { var j = JSON.parse(body); msg = j.description || j.message || ''; } catch (e) { msg = body.slice(0, 160); }
    return { ok: res.ok, status: res.status, message: msg };
  }
  /* What's already in the cart, by variant id and by product handle. */
  async function cartVariantQty() {
    var out = { byVariant: Object.create(null), byHandle: Object.create(null) };
    var cart = null;
    try { cart = await fetchJson(rootPath() + 'cart.js', { credentials: 'same-origin' }); } catch (e) {}
    ((cart && cart.items) || []).forEach(function (it) {
      out.byVariant[it.variant_id] = (out.byVariant[it.variant_id] || 0) + it.quantity;
      if (it.handle) out.byHandle[it.handle] = (out.byHandle[it.handle] || 0) + it.quantity;
    });
    return out;
  }
  /* Shopify answers 422 for several different reasons; only some of them mean "sold out". */
  function cartErrorKind(r) {
    var m = String((r && r.message) || '').toLowerCase();
    if (/maximum quantity|already in your cart|all \d+ .* (are|is) in your cart|you can only add/.test(m)) return 'max';
    if (/sold out|out of stock|no longer available|unavailable|not available/.test(m)) return 'soldout';
    return 'other';
  }
  function offerLabel(o) {
    return [o.setName, COND_LABEL[o.cond], o.foil ? 'foil' : ''].filter(Boolean).join(', ');
  }
  async function refreshCartUi() {
    // Impulse rebuilds its cart drawer and the header cart dot on "cart:build".
    try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (e) {}
    var cart = null;
    try { cart = await fetchJson(rootPath() + 'cart.js', { credentials: 'same-origin' }); } catch (e) {}
    if (!cart) return null;
    var n = cart.item_count || 0;
    $all('.cart-link__bubble-num, [data-cart-count]').forEach(function (el) { el.textContent = n; });
    $all('.cart-link__bubble').forEach(function (b) { b.classList.toggle('cart-link__bubble--visible', n > 0); });
    try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } })); } catch (e) {}
    return cart;
  }

  /* Live per-variant stock counts via the theme's bb-stock-json section (also used by the Deck Builder). */
  var qtyCache = Object.create(null);
  function stockQty(handle) {
    if (qtyCache[handle]) return qtyCache[handle];
    var p = fetch(rootPath() + 'products/' + encodeURIComponent(handle) + '?section_id=bb-stock-json', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var m = html && html.match(/<script[^>]*data-bb-stock[^>]*>([\s\S]*?)<\/script>/);
        var map = Object.create(null);
        if (m) { try { JSON.parse(m[1]).variants.forEach(function (v) { map[v.id] = v.inventory_quantity; }); } catch (e) {} }
        return map;
      })
      .catch(function () { delete qtyCache[handle]; return Object.create(null); });
    qtyCache[handle] = p;
    return p;
  }
  function qtyLabel(q) {
    if (q == null) return '';
    if (q <= 0) return '';
    return q === 1 ? 'Only 1 in stock' : q + ' in stock';
  }

  /* Keep the sticky total bar above floating widgets (rewards launcher, wishlist, preview bar). */
  function placeStickyBar(bar) {
    if (!bar || !bar.offsetParent) return;
    var br = bar.getBoundingClientRect();
    var h = window.innerHeight, lift = 0;
    $all('body *').forEach(function (el) {
      if (el === bar || bar.contains(el) || el.contains(bar)) return;
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.visibility === 'hidden' || cs.display === 'none') return;
      var r = el.getBoundingClientRect();
      if (r.height <= 0 || r.height > 160 || r.top < h * 0.6 || r.bottom < h - 40) return;
      if (r.right <= br.left || r.left >= br.right) return; // doesn't overlap horizontally
      lift = Math.max(lift, h - r.top);
    });
    bar.style.bottom = Math.min(160, Math.round(lift) + 10) + 'px';
  }

  /* ---------------- recommender ---------------- */
  /* ---------------- Commander brackets ---------------- */
  var BRACKET_NAME = { 1: 'Exhibition', 2: 'Core', 3: 'Upgraded', 4: 'Optimized', 5: 'cEDH' };
  var EARLY_COMBO_MV = 7;   // two-card combos whose pieces cost less than 7 mana total count as "early"
  var MAX_GC_B3 = 3;        // bracket 3: up to three Game Changers
  var MAX_EXTRA_TURNS = 2;  // brackets 1–3: extra turns only in low numbers (we treat 3+ as chain-able)
  var bracketDataP = null;
  function loadBracketData(comboUrl) {
    if (bracketDataP) return bracketDataP;
    bracketDataP = (async function () {
      var out = { gc: Object.create(null), mld: Object.create(null), xt: Object.create(null), combos: Object.create(null),
        gcSource: '', comboSource: '', built: '', ok: false };
      var snap = null;
      if (comboUrl) snap = await fetchJson(comboUrl, { credentials: 'omit', timeout: 20000 }).catch(function () { return null; });
      // Game Changers: live from Scryfall; fall back to the snapshot if Scryfall is down.
      var live = await scryfallSearch('is:gamechanger', 2).catch(function () { return []; });
      out.gcCards = [];
      if (live.length >= 20) { live.forEach(function (c) { out.gc[frontKey(c.name)] = c.name; }); out.gcCards = live; out.gcSource = 'Scryfall (live)'; }
      else if (snap && snap.gameChangers) { snap.gameChangers.forEach(function (n) { out.gc[frontKey(n)] = n; }); out.gcSource = 'Commander Spellbook snapshot'; }
      if (snap && snap.pairs) {
        (snap.massLandDenial || []).forEach(function (n) { out.mld[frontKey(n)] = n; });
        (snap.extraTurn || []).forEach(function (n) { out.xt[frontKey(n)] = n; });
        var names = snap.cards, res = snap.results;
        snap.pairs.forEach(function (r) {
          var a = names[r[0]], b = names[r[1]];
          var rec = { a: a, b: b, ka: frontKey(a), kb: frontKey(b), tag: r[2], mana: r[3], result: res[r[4]], id: r[5], cmdr: r[6] === 1 ? frontKey(a) : r[6] === 2 ? frontKey(b) : '' };
          (out.combos[rec.ka] = out.combos[rec.ka] || []).push(rec);
          (out.combos[rec.kb] = out.combos[rec.kb] || []).push(rec);
        });
        out.comboSource = 'Commander Spellbook';
        out.built = snap.built || '';
      }
      out.ok = !!(out.gcSource && out.comboSource);
      return out;
    })();
    bracketDataP.catch(function () { bracketDataP = null; });
    return bracketDataP;
  }
  /* Commander precon decklists (MTGJSON). The server copy is rebuilt weekly (bb-precons.timer on bb-search-1)
     and also has the full lists for the precon picker; the theme asset (tools/build_precons.py) is the fallback. */
  var preconRawP = null;
  function loadPreconRaw(root) {
    if (preconRawP) return preconRawP;
    var urls = [root.getAttribute('data-precons-live-url'), root.getAttribute('data-precons-url')].filter(Boolean);
    preconRawP = (async function () {
      for (var i = 0; i < urls.length; i++) {
        var d = await fetchJson(urls[i], { credentials: 'omit', timeout: 15000 }).catch(function () { return null; });
        if (d && d.decks && d.names) return d;
      }
      return null;
    })();
    preconRawP.then(function (d) { if (!d) preconRawP = null; });
    return preconRawP;
  }
  var preconP = null;
  function loadPrecons(root) {
    if (preconP) return preconP;
    preconP = (async function () {
      var d = await loadPreconRaw(root);
      if (!d || !d.decks) return null;
      var keys = d.names.map(frontKey);
      // v2 lists Collector's Editions etc. too (x: same list as an earlier deck); match each list once.
      var src = d.decks.filter(function (dk) { return !dk.x; });
      var out = src.map(function (dk) {
        var set = Object.create(null), faces = Object.create(null);
        dk.c.forEach(function (i) { set[keys[i]] = d.names[i]; });
        (dk.cm || []).forEach(function (i) { faces[keys[i]] = true; });
        return { name: dk.n, set: dk.s, cards: set, faces: faces };
      });
      Object.keys(d.cmdr || {}).forEach(function (n) { d.cmdr[n].forEach(function (i) { if (out[i]) out[i].faces[frontKey(n)] = true; }); });
      // How many precons each card is in: Sol Ring, Arcane Signet, Command Tower… are in nearly all of them.
      var freq = Object.create(null);
      out.forEach(function (dk) { Object.keys(dk.cards).forEach(function (k) { freq[k] = (freq[k] || 0) + 1; }); });
      out.freq = freq;
      return out;
    })();
    preconP.then(function (v) { if (!v) preconP = null; }, function () { preconP = null; });
    return preconP;
  }
  // The precon this deck was built from: one that includes a commander and shares 30+ cards with the list.
  // Its cards the player left out were cut on purpose, but EDHREC rates them very highly because most
  // decks for a precon commander are close to the precon, so they'd come straight back as "upgrades".
  function matchPrecon(decks, cmdrKeys, deckKeys) {
    var best = null;
    (decks || []).forEach(function (dk) {
      if (!Object.keys(cmdrKeys).some(function (k) { return dk.cards[k]; })) return;
      var n = 0;
      Object.keys(dk.cards).forEach(function (k) { if (deckKeys[k]) n++; });
      var face = Object.keys(cmdrKeys).some(function (k) { return dk.faces[k]; });
      if (!best || n > best.overlap) best = { deck: dk, overlap: n, face: face };
    });
    return best;
  }

  // Format staples stay recommendable even when the precon had them: anything in the top ~250 Commander
  // cards overall, or in 15%+ of all precons (Sol Ring, Arcane Signet, signets, Command Tower, Swords…).
  var STAPLE_RANK = 250, STAPLE_PRECON_SHARE = 0.15;
  function isStaple(decks, k, card) {
    if (card && card.edhrec_rank && card.edhrec_rank <= STAPLE_RANK) return true;
    return !!(decks && decks.freq && (decks.freq[k] || 0) >= decks.length * STAPLE_PRECON_SHARE);
  }

  function isGameChanger(bd, key, card) { return !!(bd.gc[key] || (card && card.game_changer)); }
  // A two-card combo is "early" (assemblable before about turn 6) if both pieces together cost under 7 mana.
  function comboEarly(cmcSum) { return cmcSum != null && cmcSum < EARLY_COMBO_MV; }
  function comboOk(rec, cmdrKeys) { return !rec.cmdr || !!cmdrKeys[rec.cmdr]; }

  /* Analyse a pasted Commander deck. cards: { key: scryfallCard }, cmdrKeys: { key: true } */
  function analyzeDeck(bd, cards, cmdrKeys) {
    var keys = Object.keys(cards);
    var a = { gc: [], mld: [], xt: [], combos: [], early: [], reasons: [] };
    keys.forEach(function (k) {
      var c = cards[k];
      if (isGameChanger(bd, k, c)) a.gc.push(c.name);
      if (bd.mld[k]) a.mld.push(c.name);
      if (bd.xt[k]) a.xt.push(c.name);
    });
    var seen = Object.create(null);
    keys.forEach(function (k) {
      (bd.combos[k] || []).forEach(function (rec) {
        if (seen[rec.id] || !cards[rec.ka] || !cards[rec.kb] || !comboOk(rec, cmdrKeys)) return;
        seen[rec.id] = 1;
        var mv = (cards[rec.ka].cmc || 0) + (cards[rec.kb].cmc || 0);
        var x = { a: cards[rec.ka].name, b: cards[rec.kb].name, mv: mv, result: rec.result, tag: rec.tag, early: comboEarly(mv) };
        a.combos.push(x);
        if (x.early) a.early.push(x);
      });
    });
    a.combos.sort(function (x, y) { return (y.early - x.early) || (x.mv - y.mv); });
    // Estimate (WotC bracket rules). We can't judge intent, so 1 vs 2 and 4 vs 5 are not split.
    var est = 2;
    if (a.gc.length || a.combos.length) est = 3;
    if (a.gc.length > MAX_GC_B3) a.reasons.push(a.gc.length + ' Game Changers (bracket 3 allows ' + MAX_GC_B3 + ')');
    if (a.early.length) a.reasons.push(a.early.length + ' early two-card combo' + (a.early.length > 1 ? 's' : ''));
    if (a.mld.length) a.reasons.push('mass land denial (' + a.mld.slice(0, 3).join(', ') + ')');
    if (a.xt.length > MAX_EXTRA_TURNS) a.reasons.push(a.xt.length + ' extra-turn cards');
    if (a.reasons.length) est = 4;
    else if (est === 3) {
      if (a.gc.length) a.reasons.push(a.gc.length + ' Game Changer' + (a.gc.length > 1 ? 's' : ''));
      if (a.combos.length) a.reasons.push('a late two-card combo');
    }
    a.est = est;
    // Lowest bracket each rule allows (for "above your bracket" warnings).
    a.over = function (b) {
      var why = [];
      if (!b || b >= 4) return why;
      if (b <= 2 && a.gc.length) why.push(a.gc.length + ' Game Changer' + (a.gc.length > 1 ? 's' : '') + ' (none allowed in bracket ' + b + ')');
      if (b === 3 && a.gc.length > MAX_GC_B3) why.push(a.gc.length + ' Game Changers (max ' + MAX_GC_B3 + ')');
      if (b <= 2 && a.combos.length) why.push('two-card infinite combo' + (a.combos.length > 1 ? 's' : '') + ' (none allowed in bracket ' + b + ')');
      else if (a.early.length) why.push('early two-card combo' + (a.early.length > 1 ? 's' : ''));
      if (a.mld.length) why.push('mass land denial: ' + a.mld.join(', '));
      if (a.xt.length > MAX_EXTRA_TURNS) why.push(a.xt.length + ' extra-turn cards (keep it to a couple, never chained)');
      return why;
    };
    return a;
  }

  /* Why a candidate can't be added at this bracket ('' = fine). picked: [{key, card}] already chosen this fit. */
  function bracketBlock(bd, b, c, deck, cmdrKeys, picked, counts) {
    if (!bd || !b || b >= 4) return '';
    var k = c.key, card = c.card;
    if (isGameChanger(bd, k, card)) {
      if (b <= 2) return 'Game Changer — not allowed in bracket ' + b;
      if (counts.gc >= MAX_GC_B3) return 'Game Changer — your deck already has ' + counts.deckGc + (counts.gc > counts.deckGc ? ' (+' + (counts.gc - counts.deckGc) + ' picked)' : '') + ', bracket 3 allows ' + MAX_GC_B3;
    }
    if (bd.mld[k]) return 'Mass land denial — not allowed below bracket 4';
    if (bd.xt[k] && counts.xt >= MAX_EXTRA_TURNS) return 'Extra turns — you’d have ' + (counts.xt + 1) + ', which can chain';
    var combos = bd.combos[k] || [];
    for (var i = 0; i < combos.length; i++) {
      var rec = combos[i];
      var other = rec.ka === k ? rec.kb : rec.ka;
      var oc = deck[other] || null, inPicks = null;
      if (!oc) { for (var j = 0; j < picked.length; j++) if (picked[j].key === other) { inPicks = picked[j]; break; } }
      if (!oc && !inPicks) continue;
      if (!comboOk(rec, cmdrKeys)) continue;
      var otherCard = oc || (inPicks && inPicks.card);
      var mv = (card && card.cmc != null && otherCard && otherCard.cmc != null) ? card.cmc + otherCard.cmc : null;
      var with_ = (otherCard && otherCard.name) || other;
      if (b <= 2) return 'Makes a two-card infinite with ' + with_ + ' — not allowed in bracket ' + b;
      if (comboEarly(mv == null ? 0 : mv)) return 'Early two-card combo with ' + with_ + (mv != null ? ' (' + mv + ' mana total)' : '') + ' — not allowed in bracket 3';
    }
    return '';
  }

  /* ---- Building toward a higher bracket ----
     Fast mana and free/cheap interaction are hand-picked lists of the usual cEDH staples (no public
     source tags these), and so are efficient tutors (Spellbook's tutor flag also covers narrow
     tribal/creature tutors); Game Changers and combos as above. */
  function keySet(list) { var o = Object.create(null); list.forEach(function (n) { o[frontKey(n)] = n; }); return o; }
  var FAST_MANA = keySet(['Mana Vault', 'Grim Monolith', 'Chrome Mox', 'Mox Diamond', 'Mox Opal', 'Mox Amber', 'Lotus Petal',
    'Jeweled Lotus', "Lion's Eye Diamond", 'Ancient Tomb', 'Sol Ring', 'Mana Crypt', "Mishra's Workshop", "Gaea's Cradle", 'Gemstone Caverns',
    'Simian Spirit Guide', 'Elvish Spirit Guide', 'Rite of Flame', 'Pyretic Ritual', 'Desperate Ritual', 'Seething Song', 'Dark Ritual',
    'Cabal Ritual', 'Culling the Weak', 'Springleaf Drum', 'Arcane Signet', 'Fellwar Stone', 'Talisman of Creativity', 'Talisman of Dominance',
    'Talisman of Progress', 'Talisman of Indulgence', 'Talisman of Conviction', 'Talisman of Curiosity', 'Talisman of Resilience',
    'Talisman of Hierarchy', 'Talisman of Impulse', 'Talisman of Unity', 'Coldsteel Heart', 'Mox Tantalite', 'Strike It Rich',
    'Carpet of Flowers', 'Utopia Sprawl', 'Wild Growth', 'Birds of Paradise', 'Llanowar Elves', 'Elvish Mystic', 'Fyndhorn Elves',
    'Delighted Halfling', 'Avacyn\'s Pilgrim', 'Noble Hierarch', 'Bloom Tender', 'Mox Opal', 'Paradise Mantle', 'Lotus Bloom', 'Dark Ritual']);
  /* True free spells: each can be cast for no mana (alternate / pitch / evoke / Phyrexian / conditional free cost), checked against Scryfall oracle text. */
  var FREE_INTERACTION = keySet([
    'Force of Will', 'Force of Negation', 'Fierce Guardianship', 'Pact of Negation', 'Mental Misstep', 'Mindbreak Trap', 'Daze',
    'Misdirection', 'Commandeer', 'Foil', 'Thwart', 'Deflecting Swat', 'Deadly Rollick', 'Flawless Maneuver', 'Snuff Out', 'Subtlety',
    'Solitude', 'Endurance', 'Grief', 'Fury', 'Pyrokinesis', 'Mogg Salvage', 'Gush', 'Disrupting Shoal', 'Sickening Shoal',
    'Blazing Shoal']);
  /* Cheap interaction: one or two mana, but never free. Ranked exactly like FREE_INTERACTION, just tagged differently. */
  var CHEAP_INTERACTION = keySet([
    'Flusterstorm', 'Spell Pierce', 'Swan Song', 'Pyroblast', 'Red Elemental Blast', 'Stern Scolding', 'Tishana\'s Tidebinder',
    'An Offer You Can\'t Refuse', 'Silence', 'Veil of Summer', 'Autumn\'s Veil', 'Dispel', 'Mystical Dispute', 'Delay',
    'Borne Upon a Wind', 'Orim\'s Chant', 'Cyclonic Rift', 'Chain of Vapor', 'Into the Roil']);
  var TUTOR_EXTRA = keySet(['Mystical Tutor', 'Vampiric Tutor', 'Demonic Tutor', 'Imperial Seal', 'Enlightened Tutor', 'Worldly Tutor',
    'Gamble', 'Personal Tutor', 'Merchant Scroll', 'Solve the Equation', 'Muddle the Mixture', 'Dizzy Spell', 'Long-Term Plans',
    'Intuition', 'Gifts Ungiven', 'Fabricate', 'Whir of Invention', 'Reshape', 'Transmute Artifact', 'Spellseeker', 'Imperial Recruiter',
    'Recruiter of the Guard', 'Trinket Mage', 'Tribute Mage', 'Wishclaw Talisman', 'Tainted Pact', 'Demonic Consultation',
    'Diabolic Intent', 'Grim Tutor', 'Sylvan Tutor', "Eladamri's Call", 'Chord of Calling', "Green Sun's Zenith", 'Finale of Devastation',
    'Natural Order', 'Crop Rotation', 'Entomb', 'Buried Alive', 'Idyllic Tutor', "Steelshaper's Gift", 'Final Parting',
    "Lim-Dûl's Vault", 'Scheming Symmetry', 'Beseech the Mirror', 'Insidious Dreams', 'Urza\'s Saga', 'Inventors\' Fair',
    'Tolaria West', 'Step Through', 'Brainspoil', 'Dimir Machinations', 'Shred Memory']);
  // Boosts added to a card's score when building toward a higher bracket (EDHREC scores run ~0–1).
  var PUSH = {
    3: { gc: 0.55, fast: 0.12, free: 0.1, tutor: 0.12, combo: 0, early: 0, compact: 0, bigPenalty: 0 },
    4: { gc: 0.6, fast: 0.45, free: 0.35, tutor: 0.4, combo: 0.5, early: 0.15, compact: 0.05, bigPenalty: 0.05 },
    5: { gc: 0.8, fast: 0.75, free: 0.65, tutor: 0.65, combo: 0.85, early: 0.3, compact: 0.15, bigPenalty: 0.2 }
  };
  function powerFlags(bd, c, deck, cmdrKeys, candMap) {
    var k = c.key || frontKey(c.name), card = c.card;
    var f = { gc: isGameChanger(bd, k, card), fast: !!FAST_MANA[k], free: !!(FREE_INTERACTION[k] || CHEAP_INTERACTION[k]), cheap: !!CHEAP_INTERACTION[k],
      tutor: !!TUTOR_EXTRA[k], combos: [], pairs: [] };
    (bd.combos[k] || []).forEach(function (rec) {
      var other = rec.ka === k ? rec.kb : rec.ka;
      if (!deck[other] && candMap && candMap[other] && !deck[k] && !(rec.cmdr && !cmdrKeys[rec.cmdr])) {
        var pmv = card && card.cmc != null ? card.cmc + (candMap[other].cmc || 0) : null;
        f.pairs.push({ key: other, with: candMap[other].name, mv: pmv, early: comboEarly(pmv == null ? 0 : pmv), result: rec.result });
        return;
      }
      if (!deck[other] || deck[k]) return;
      if (rec.cmdr && !cmdrKeys[rec.cmdr]) return;
      var mv = card && card.cmc != null ? card.cmc + (deck[other].cmc || 0) : null;
      f.combos.push({ with: deck[other].name, withCmdr: !!cmdrKeys[other], mv: mv, early: comboEarly(mv == null ? 0 : mv), result: rec.result });
    });
    f.combos.sort(function (a, b) { return (b.early - a.early) || ((a.mv || 99) - (b.mv || 99)); });
    f.pairs.sort(function (a, b) { return (b.early - a.early) || ((a.mv || 99) - (b.mv || 99)); });
    return f;
  }
  // Push when the target is above the estimate, always for 5 (cEDH tuning), and for 3 while there are free Game Changer slots.
  function pushMode(target, a) { return !!target && !!a && (target > a.est || target === 5 || (target === MAX_GC_B3 && a.gc.length < MAX_GC_B3)); }
  // Boost for completing a combo with another suggestion. Applied in computeFit only after the partner was picked (so it's in stock).
  function pairBoost(c, target) {
    var t = PUSH[target], f = c.pf;
    if (!t || !t.combo || !f || !f.pairs || !f.pairs.length || f.combos.length) return 0;
    var pb = f.pairs[0];
    return 0.7 * (t.combo + (pb.early ? t.early : 0) + (pb.mv != null && pb.mv <= 4 ? t.compact : 0));
  }
  function powerBoost(c, target) {
    var t = PUSH[target], f = c.pf;
    if (!t || !f) return { add: 0, why: '' };
    var parts = [], why = [];
    // A Game Changer EDHREC never sees with this commander is likely a poor fit (Field of the Dead in a spells deck), so only a small boost.
    if (f.gc) { parts.push(c.whyBase ? t.gc : t.gc * 0.25); why.push('Game Changer'); }
    if (f.fast) { parts.push(t.fast); why.push('fast mana'); }
    if (f.free) { parts.push(t.free); why.push(f.cheap ? 'cheap interaction' : 'free interaction'); }
    if (f.tutor) { parts.push(t.tutor); why.push('efficient tutor'); }
    if (t.combo && f.combos.length) {
      var cb = f.combos[0];
      parts.push(t.combo + (cb.early ? t.early : 0) + (cb.mv != null && cb.mv <= 4 ? t.compact : 0) + (cb.withCmdr ? 0.1 : 0));
      why.push('completes ' + (cb.early ? 'an early ' : 'a ') + 'combo with ' + cb.with + (cb.mv != null ? ' (' + cb.mv + ' mana)' : '') + ': ' + String(cb.result || '').toLowerCase());
    }
    if (!parts.length) {
      var card = c.card;
      var pen = card && !isLandCard(card) && card.cmc >= 6 ? t.bigPenalty : 0;
      return { add: -pen, why: '' };
    }
    parts.sort(function (a, b) { return b - a; });
    var add = parts[0] + 0.15 * parts.slice(1).reduce(function (n, x) { return n + x; }, 0);
    return { add: add, why: why.join(' · ').replace(/^./, function (x) { return x.toUpperCase(); }) };
  }

  /* ---- Mana base lane: premium lands that replace a basic, 1-for-1 ---- */
  var LAND_TIERS = [
    ['Fetch land', ['Flooded Strand', 'Polluted Delta', 'Bloodstained Mire', 'Wooded Foothills', 'Windswept Heath', 'Marsh Flats',
      'Scalding Tarn', 'Verdant Catacombs', 'Arid Mesa', 'Misty Rainforest', 'Prismatic Vista']],
    ['Original dual', ['Tundra', 'Underground Sea', 'Badlands', 'Taiga', 'Savannah', 'Scrubland', 'Volcanic Island', 'Bayou', 'Plateau', 'Tropical Island']],
    ['Shock land', ['Hallowed Fountain', 'Watery Grave', 'Blood Crypt', 'Stomping Ground', 'Temple Garden', 'Godless Shrine', 'Steam Vents',
      'Overgrown Tomb', 'Sacred Foundry', 'Breeding Pool']],
    ['Triome', ['Raffine\'s Tower', 'Xander\'s Lounge', 'Ziatora\'s Proving Ground', 'Jetmir\'s Garden', 'Spara\'s Headquarters',
      'Savai Triome', 'Ketria Triome', 'Indatha Triome', 'Raugrin Triome', 'Zagoth Triome']],
    ['Utility land', ['Otawara, Soaring City', 'Sokenzan, Crucible of Defiance', 'Eiganjo, Seat of the Empire', 'Takenuma, Abandoned Mire',
      'Boseiju, Who Endures', 'Arena of Glory', 'Mana Confluence', 'City of Brass', 'Reflecting Pool', 'Cavern of Souls', 'Minamo, School at Water\'s Edge']],
    ['Horizon land', ['Fiery Islet', 'Sunbaked Canyon', 'Horizon Canopy', 'Nurturing Peatland', 'Silent Clearing', 'Waterlogged Grove']],
    ['Fast land', ['Seachrome Coast', 'Darkslick Shores', 'Blackcleave Cliffs', 'Copperline Gorge', 'Razorverge Thicket', 'Concealed Courtyard',
      'Spirebluff Canal', 'Blooming Marsh', 'Inspiring Vantage', 'Botanical Sanctum']],
    ['Check land', ['Glacial Fortress', 'Drowned Catacomb', 'Dragonskull Summit', 'Rootbound Crag', 'Sunpetal Grove', 'Isolated Chapel',
      'Sulfur Falls', 'Woodland Cemetery', 'Clifftop Retreat', 'Hinterland Harbor']],
    ['Pain land', ['Adarkar Wastes', 'Underground River', 'Sulfurous Springs', 'Karplusan Forest', 'Brushland', 'Caves of Koilos',
      'Shivan Reef', 'Llanowar Wastes', 'Battlefield Forge', 'Yavimaya Coast']],
    ['Dual land', ['Thundering Falls', 'Riverpyre Verge', 'Training Center', 'Morphic Pool', 'Undergrowth Stadium', 'Vault of Champions',
      'Luxury Suite', 'Spectator Seating', 'Rejuvenating Springs', 'Sea of Clouds', 'Bountiful Promenade', 'Hedge Maze', 'Lush Oasis',
      'Gloomlake Verge', 'Hushwood Verge', 'Thornspire Verge', 'Floodfarm Verge', 'Wastewood Verge', 'Bleachbone Verge', 'Blazemire Verge',
      'Meticulous Archive', 'Undercity Sewers', 'Raucous Theater', 'Commercial District', 'Lush Portico', 'Shadowy Backstreet',
      'Elegant Parlor', 'Underground Mortuary']]
  ];
  var LAND_TIER = Object.create(null), LAND_TIER_NAME = [];
  LAND_TIERS.forEach(function (t, i) { LAND_TIER_NAME[i] = t[0]; t[1].forEach(function (n) { LAND_TIER[frontKey(n)] = i; }); });
  var LANE_OTHER_TIER = LAND_TIERS.length;
  var LANE_BUDGET_SHARE = 0.6;  // the mana base lane may use up to 60% of the budget before spells
  var BASIC_OF = { W: 'Plains', U: 'Island', B: 'Swamp', R: 'Mountain', G: 'Forest' };
  var COLOR_OF_BASIC = { Plains: 'W', Island: 'U', Swamp: 'B', Mountain: 'R', Forest: 'G' };
  // Which basic types a land can fetch or which colors it makes; used to pick the basic it replaces.
  function landBasics(card, colors) {
    var o = oracleOf(card);
    var fetch = [];
    var m = o.match(/search your library for an? ([^.]*?) card/i);
    if (m && /sacrifice/i.test(o)) {
      if (/basic land/i.test(m[1])) fetch = colors.map(function (x) { return BASIC_OF[x]; }).filter(Boolean);
      else ['Plains', 'Island', 'Swamp', 'Mountain', 'Forest'].forEach(function (b) { if (new RegExp('\\b' + b + '\\b', 'i').test(m[1])) fetch.push(b); });
    }
    if (fetch.length) return { fetch: true, types: fetch.filter(function (b) { return colors.indexOf(COLOR_OF_BASIC[b]) !== -1; }), all: fetch };
    var prod = (card.produced_mana || []).filter(function (x) { return colors.indexOf(x) !== -1; });
    if (!card.produced_mana && card.card_faces) card.card_faces.forEach(function (f) { (f.produced_mana || []).forEach(function (x) { if (colors.indexOf(x) !== -1 && prod.indexOf(x) === -1) prod.push(x); }); });
    if (/any color|one mana of any color/i.test(o)) prod = colors.slice();
    return { fetch: false, types: prod.map(function (x) { return BASIC_OF[x]; }).filter(Boolean), all: [] };
  }
  function sfCents(card) {
    var pr = card && card.prices, v = [];
    if (pr) ['usd', 'usd_foil', 'usd_etched'].forEach(function (k) { var x = parseFloat(pr[k]); if (x > 0) v.push(x); });
    return v.length ? Math.round(Math.min.apply(null, v) * 100) : 0;
  }
  // A lane land is only worth a slot if it really fixes this deck's colors (2+ on-color types), is a
  // non-fixing utility land, or players of this commander actually run it (EDHREC 5%+).
  var ANY_COLOR_FIXERS = { 'reflecting pool': 1, 'city of brass': 1, 'mana confluence': 1, 'exotic orchard': 1, 'command tower': 1 };
  function laneWorth(card, lb, tierName, info, nColors) {
    if (nColors < 2 && ANY_COLOR_FIXERS[frontKey(card.name)]) return false; // a worse basic in a mono-color deck
    if (info && info.inclusion >= 0.05) return true;
    if (lb.types.length >= 2) return true;
    if (tierName === 'Utility land' && !ANY_COLOR_FIXERS[frontKey(card.name)]) return true;
    return false;
  }
  function basicType(name) { var m = BASIC_RE.exec(ws(name)); return m ? m[2].charAt(0).toUpperCase() + m[2].slice(1).toLowerCase() : ''; }

  function Upgrader(root) {
    this.root = root;
    this.els = {
      list: $('[data-bb-du-list]', root), copy: $('[data-bb-du-copy]', root), format: $('[data-bb-du-format]', root),
      cmdr: $('[data-bb-du-commander]', root), cmdrWrap: $('[data-bb-du-commander-wrap]', root), max: $('[data-bb-du-max]', root),
      custom: $('[data-bb-du-custom]', root), finish: $('[data-bb-du-finish]', root), cuts: $('[data-bb-du-cuts]', root),
      run: $('[data-bb-du-run]', root), clear: $('[data-bb-du-clear]', root), status: $('[data-bb-du-status]', root),
      progress: $('[data-bb-du-progress]', root), bar: $('[data-bb-du-progress-bar]', root), results: $('[data-bb-du-results]', root),
      summary: $('[data-bb-du-summary]', root), picks: $('[data-bb-du-picks]', root), outside: $('[data-bb-du-outside]', root),
      cutlist: $('[data-bb-du-cutlist]', root), total: $('[data-bb-du-total]', root), meter: $('[data-bb-du-meter]', root),
      addAll: $('[data-bb-du-addall]', root), totalSub: $('[data-bb-du-total-sub]', root), meterAdded: $('[data-bb-du-meter-added]', root),
      viewCart: $('[data-bb-du-viewcart]', root), barMsg: $('[data-bb-du-barmsg]', root),
      headCart: $('[data-bb-du-headcart]', root), headCartN: $('[data-bb-du-headcart-n]', root), resume: $('[data-bb-du-resume]', root),
      bracket: $('[data-bb-du-bracket]', root), bracketWrap: $('[data-bb-du-bracket-wrap]', root),
      precon: $('[data-bb-du-precon-input]', root), preconList: $('[data-bb-du-precon-list]', root)
    };
    this.runLabel = this.els.run.textContent;
    this.ctx = null;
    this.excluded = Object.create(null);
    this.added = Object.create(null);
    this.qty = Object.create(null); // bb-du qty0 v1: per-pick quantity (0 = shopper already owns it)
    this.stock = Object.create(null);
    this.addedQty = Object.create(null);
    this.busy = false;
    this.runId = 0;
    this.roleOn = Object.create(null); this.sortBy = 'best'; this.roleIdx = null; // bb-du roles v1
    this.statsOff = Object.create(null); // bb-du stats v1: upgrades the shopper unchecked from the deck stats
    if (this.els.picks) {
      this.els.rolebar = document.createElement('div');
      this.els.rolebar.className = 'bb-du__rolebar'; this.els.rolebar.hidden = true;
      this.els.picks.parentNode.insertBefore(this.els.rolebar, this.els.picks);
      /* bb-du stats v1: live deck stats panel (before / after the checked upgrades) */
      this.els.stats = document.createElement('details');
      this.els.stats.className = 'bb-ds'; this.els.stats.hidden = true; this.els.stats.setAttribute('data-bb-du-stats', '');
      this.els.stats.open = true;
      this.els.stats.innerHTML = '<summary class="bb-ds__sum"><span class="bb-ds__title">Deck stats</span><span class="bb-ds__head" data-bb-ds-head></span></summary><div class="bb-ds__body" data-bb-ds-body></div>';
      this.els.picks.parentNode.insertBefore(this.els.stats, this.els.rolebar);
    }
    this.bind();
    this.bindRoles();
  }

  Upgrader.prototype.state = function () {
    var r = $('[data-bb-du-budget]:checked', this.root);
    var budget = r ? r.value : '25';
    if (budget === 'custom') budget = parseFloat(this.els.custom.value || '0');
    else budget = parseFloat(budget);
    var cents = Math.max(0, Math.round((isFinite(budget) ? budget : 0) * 100));
    // "Auto": no count limit; the budget and the quality bar decide (see computeFit).
    var maxV = this.els.max.value;
    var max = maxV === 'auto' ? AUTO_CEILING : (parseInt(maxV, 10) || 10);
    return {
      format: this.els.format.value,
      budget: cents,
      max: max,
      maxAuto: maxV === 'auto',
      bracket: this.els.format.value === 'commander' && this.els.bracket ? (parseInt(this.els.bracket.value, 10) || 0) : 0,
      conds: $all('[data-bb-du-condition]:checked', this.root).map(function (e) { return e.value; }),
      finish: this.els.finish.value,
      cuts: !!(this.els.cuts && this.els.cuts.checked)
    };
  };

  Upgrader.prototype.bind = function () {
    var self = this;
    var dbList = document.querySelector('[data-bb-db-list]');
    this.dbList = dbList;
    if (dbList && this.els.copy) {
      this.els.copy.hidden = false;
      this.els.copy.addEventListener('click', function () {
        if (dbList.value.trim()) { self.els.list.value = dbList.value; self.cmdrOverride = null; self.setPreconLabel(''); self.els.cmdr.value = ''; self.markListError(false); self.setStatus('Copied your list from the Deck Builder above.', 'ok'); }
        else { self.setStatus('The Deck Builder list above is empty. Paste your deck here instead.', 'error'); self.els.list.focus(); }
      });
    }
    this.els.list.addEventListener('input', function () { self.cmdrOverride = null; self.setPreconLabel(''); if (self.els.list.value.trim()) self.markListError(false); });
    this.bindPrecon();
    this.els.cmdr.addEventListener('input', function () { self.cmdrOverride = null; });
    this.root.addEventListener('change', function (e) {
      if (!e.target.matches('[data-bb-du-cmdr-pick]')) return;
      self.cmdrOverride = e.target.value;
      self.els.cmdr.value = '';
      self.run();
    });
    // "View cart" is a plain link to the cart page (keeps ?preview_theme_id so theme previews stay in that theme).
    if (this.els.viewCart) this.els.viewCart.setAttribute('href', cartUrl());
    this.els.run.addEventListener('click', function () { self.run(); });
    this.els.clear.addEventListener('click', function () {
      self.runId++; self.busy = false; self.setRunBusy(false);
      self.els.list.value = ''; self.els.cmdr.value = ''; self.cmdrOverride = null; self.setPreconLabel(''); self.els.cmdr.placeholder = 'Auto-detected from your list';
      self.roleOn = Object.create(null); self.sortBy = 'best';
      self.ctx = null; self.fit = null; self.excluded = Object.create(null); self.added = Object.create(null); self.qty = Object.create(null); self.addedQty = Object.create(null); self.statsOff = Object.create(null);
      if (self.els.stats) self.els.stats.hidden = true;
      self.els.results.hidden = true; self.setStatus(''); self.setProgress(0, 0); self.markListError(false); self.els.list.focus();
      self.showResume(false);
      storeDel();
    });
    this.els.format.addEventListener('change', function () { self.syncFormatUi(); });
    this.root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-bb-du-condition], [data-bb-du-budget]')) self.syncChips();
      if (t.matches('[data-bb-du-condition], [data-bb-du-budget], [data-bb-du-finish], [data-bb-du-max], [data-bb-du-custom], [data-bb-du-cuts], [data-bb-du-bracket]')) {
        if (self.ctx && !self.els.run.disabled) self.refit();
      }
    });
    function pickCustom() {
      var r = self.els.custom.closest('label').querySelector('input[type=radio]');
      if (r && !r.checked) { r.checked = true; self.syncChips(); }
    }
    // Typing an amount (or focusing the box) selects the Custom chip.
    this.els.custom.addEventListener('focus', pickCustom);
    this.els.custom.addEventListener('input', pickCustom);
    this.root.addEventListener('click', function (e) {
      var pv = e.target.closest('[data-bb-du-preview]');
      if (pv) { e.preventDefault(); self.openPreview(pv.getAttribute('data-bb-du-preview'), pv.getAttribute('data-key'), pv); return; }
      var b = e.target.closest('[data-bb-du-act]');
      if (!b) return;
      var act = b.getAttribute('data-bb-du-act');
      var key = b.getAttribute('data-key');
      if (act === 'add') self.addItems([key], b);
      else if (act === 'remove') { self.excluded[key] = true; self.refit(); }
      else if (act === 'include') self.includeOutside([key]);
      else if (act === 'include-all') self.includeOutside(null);
      else if (act === 'more-open') { self.moreOpen = true; self.render(self.fit, false); var nb = $('.bb-du__more .bb-du__card:nth-child(' + (MORE_SHOWN + 1) + ') .bb-du__namebtn', self.root); if (nb) nb.focus(); }
    });
    this.root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t.matches || !t.matches('[data-bb-du-incl]')) return;
      self.statsOff[t.getAttribute('data-key')] = !t.checked;
      self.renderStats();
    });
    function onQty(e, commit) {
      var inp = e.target;
      if (!inp.matches || !inp.matches('[data-bb-du-setqty]')) return;
      var p = self.pickByKey(inp.getAttribute('data-key'));
      if (!p) return;
      var v = parseInt(inp.value, 10);
      if (!isFinite(v)) { if (!commit) return; v = self.qtyOf(p); }
      v = Math.max(0, Math.min(v, self.qtyCap(p)));
      self.qty[p.cand.key] = v;
      if (commit || String(v) !== inp.value) inp.value = String(v);
      self.syncQtyCard(p);
      self.renderTotals(false);
      self.renderStats();
    }
    this.root.addEventListener('input', function (e) { onQty(e, false); });
    this.root.addEventListener('change', function (e) { onQty(e, true); });
    if (this.els.addAll) this.els.addAll.addEventListener('click', function () {
      self.addItems((self.fit ? self.fit.picks : []).map(function (p) { return p.cand.key; }), self.els.addAll);
    });
    // Remember the form between visits (refresh, back from the cart, etc.).
    var restored = this.restoreState();
    var saveT = null;
    function saveSoon() { clearTimeout(saveT); saveT = setTimeout(function () { self.saveState(); }, 300); }
    this.saveSoon = saveSoon;
    this.els.list.addEventListener('input', saveSoon);
    this.els.cmdr.addEventListener('input', saveSoon);
    this.els.custom.addEventListener('input', saveSoon);
    this.root.addEventListener('change', function (e) { if (!e.target.closest('.bb-du-preview') && !e.target.closest('[data-bb-du-precon]')) saveSoon(); });
    window.addEventListener('pagehide', function () { if (self.els.list.value.trim()) self.saveState(); });
    if (this.els.resume) this.els.resume.addEventListener('click', function (e) {
      if (e.target.closest('[data-bb-du-resume-run]')) self.run();
    });
    if (restored && this.els.list.value.trim()) this.showResume(true);
    // "View cart" is always reachable from the section header; it shows the item count when there is one.
    if (this.els.headCart) this.els.headCart.setAttribute('href', cartUrl());
    document.addEventListener('cart:updated', function (e) { self.syncHeadCart(e.detail && e.detail.cart); });
    window.addEventListener('pageshow', function (e) { if (e.persisted) self.syncHeadCart(); });
    this.syncHeadCart();
    this.syncChips();
    this.syncFormatUi();
    var bar = $('.bb-db__sticky-bar', this.root), t = null;
    function relayout() { clearTimeout(t); t = setTimeout(function () { placeStickyBar(bar); }, 150); }
    window.addEventListener('resize', relayout);
    window.addEventListener('scroll', function () { if (!self._placed) { self._placed = true; relayout(); } }, { passive: true });
    setTimeout(relayout, 4000); // rewards/wishlist launchers load late
    // /pages/deck-builder#upgrade: land on this section again once the page above it has finished loading.
    this.openPreconFromUrl();
    if (location.hash === '#upgrade') window.addEventListener('load', function () {
      var a = document.getElementById('upgrade');
      if (a && location.hash === '#upgrade') a.scrollIntoView();
    });
  };

  /* ---- Precon picker: searchable list of every Commander precon (alternative to pasting a list) ---- */
  function foldKey(s) { return stripDiacritics(String(s || '')).toLowerCase().replace(/[‘’'`´]/g, '').replace(/[^a-z0-9]+/g, ' ').trim(); }
  var SET_SHORT = { SLD: 'Secret Lair' };
  var preconIdxP = null;
  function loadPreconIndex(root) {
    if (preconIdxP) return preconIdxP;
    preconIdxP = loadPreconRaw(root).then(function (d) {
      if (!d || !d.decks || !d.decks.some(function (dk) { return dk.l; })) return null; // fallback asset has no full lists
      return d.decks.map(function (dk, i) {
        var cm = (dk.cm || []).map(function (j) { return d.names[j]; });
        var setName = SET_SHORT[dk.s] || (d.sets && d.sets[dk.s]) || dk.s;
        var year = (dk.d || '').slice(0, 4);
        var where = setName + (year && setName.indexOf(year) === -1 ? ' · ' + year : '');
        var hay = ' ' + foldKey([dk.n, cm.join(' '), setName, dk.s, year].join(' ')) + ' ';
        return { i: i, name: dk.n, cm: cm, where: where, date: dk.d || '', hay: hay, nameK: foldKey(dk.n), cmK: cm.map(foldKey), raw: dk, names: d.names };
      }).sort(function (a, b) { return a.date < b.date ? 1 : a.date > b.date ? -1 : (a.name < b.name ? -1 : 1); });
    });
    preconIdxP.then(function (v) { if (!v) preconIdxP = null; }, function () { preconIdxP = null; });
    return preconIdxP;
  }
  function preconSearch(list, q) {
    var qk = foldKey(q);
    if (!qk) return list.slice();
    var toks = qk.split(' ');
    var hits = [];
    list.forEach(function (p) {
      var score = 0;
      for (var t = 0; t < toks.length; t++) {
        var at = p.hay.indexOf(toks[t]);
        if (at === -1) return;
        score += p.hay.indexOf(' ' + toks[t]) !== -1 ? 2 : 1; // word starts beat mid-word matches
      }
      if (p.nameK.indexOf(qk) === 0) score += 6;
      if (p.cmK.some(function (c) { return c.indexOf(qk) === 0; })) score += 5;
      hits.push({ p: p, s: score });
    });
    hits.sort(function (a, b) { return b.s - a.s || (a.p.date < b.p.date ? 1 : a.p.date > b.p.date ? -1 : 0); });
    return hits.map(function (h) { return h.p; });
  }
  function preconText(p) {
    var N = p.names, dk = p.raw;
    return 'Commander\n' + (dk.cm || []).map(function (j) { return '1 ' + N[j]; }).join('\n') +
      '\n\nDeck\n' + (dk.l || []).map(function (e) { return e[1] + ' ' + N[e[0]]; }).join('\n');
  }
  // Stable precon id for deep links (product pages link to ?precon=<id>): slug of set code + deck name.
  // Must match slug() in /workspace/precon-links/tools/build_precon_map.py.
  function preconId(dk) { return stripDiacritics((dk.s || '') + ' ' + (dk.n || '')).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  Upgrader.prototype.openPreconFromUrl = function () {
    var id = '';
    try { id = (new URLSearchParams(window.location.search).get('precon') || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 120); } catch (e) {}
    if (!id || !this.els.precon || !this.loadPrecon) return;
    var self = this;
    this.showResume(false);
    function land() {
      // Budget picker at the top of the screen; the results load right below the options.
      var b = $('[data-bb-du-budget]', self.root), blk = b && (b.closest('.bb-du__block') || b.closest('.bb-db__panel'));
      if (!blk) return;
      var y = blk.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo(0, Math.max(0, y));
    }
    this.setStatus('Loading that precon…');
    loadPreconIndex(this.root).catch(function () { return null; }).then(function (list) {
      var p = list && list.filter(function (x) { return preconId(x.raw) === id; })[0];
      if (!p) {
        self.setStatus(list ? 'We couldn’t find that precon. Search for it above, or paste your list.' : 'The precon list didn’t load. Paste your deck below instead.', 'error');
        return;
      }
      self.scrollOnDone = false;
      self.loadPrecon(p);
      // The #upgrade jump (ours and Safari's own) can land after this, so settle again unless the shopper has scrolled.
      var moved = false;
      ['wheel', 'touchstart', 'keydown'].forEach(function (ev) { window.addEventListener(ev, function () { moved = true; }, { once: true, passive: true }); });
      function settle() { if (!moved) land(); }
      settle();
      function later() { setTimeout(settle, 60); setTimeout(settle, 800); }
      if (document.readyState === 'complete') later(); else window.addEventListener('load', later);
    });
  };
  Upgrader.prototype.setPreconLabel = function (v) {
    this.preconLabel = v || '';
    if (this.els.precon && document.activeElement !== this.els.precon) this.els.precon.value = this.preconLabel;
  };
  Upgrader.prototype.bindPrecon = function () {
    var self = this, inp = this.els.precon, lb = this.els.preconList;
    if (!inp || !lb) return;
    var items = [], active = -1, open = false, seq = 0;
    function setActive(n) {
      var opts = $all('[role=option]', lb);
      if (!opts.length) { active = -1; inp.removeAttribute('aria-activedescendant'); return; }
      active = Math.max(0, Math.min(opts.length - 1, n));
      opts.forEach(function (o, k) { o.classList.toggle('is-active', k === active); o.setAttribute('aria-selected', k === active ? 'true' : 'false'); });
      inp.setAttribute('aria-activedescendant', opts[active].id);
      var o = opts[active], top = o.offsetTop, bot = top + o.offsetHeight;
      if (top < lb.scrollTop) lb.scrollTop = top; else if (bot > lb.scrollTop + lb.clientHeight) lb.scrollTop = bot - lb.clientHeight;
    }
    function close() {
      open = false; lb.hidden = true; inp.setAttribute('aria-expanded', 'false'); inp.removeAttribute('aria-activedescendant'); active = -1;
    }
    function fitList() {
      // Keep the list above a phone's on-screen keyboard.
      var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      var room = vh - inp.getBoundingClientRect().bottom - 12;
      lb.style.maxHeight = Math.round(Math.max(160, Math.min(352, room))) + 'px';
    }
    function render(msg) {
      open = true; lb.hidden = false; inp.setAttribute('aria-expanded', 'true'); fitList();
      if (msg) { lb.innerHTML = '<li class="bb-du__combo-msg" role="presentation">' + esc(msg) + '</li>'; active = -1; return; }
      var base = lb.id || 'bb-du-pc';
      lb.innerHTML = items.length ? items.map(function (p, k) {
        return '<li role="option" id="' + base + '-o' + k + '" class="bb-du__combo-opt" data-k="' + k + '" aria-selected="false">' +
          '<span class="bb-du__combo-name">' + esc(p.name) + '</span>' +
          '<span class="bb-du__combo-meta">' + (p.cm.length ? esc(p.cm.join(' & ')) + ' · ' : '') + esc(p.where) + '</span></li>';
      }).join('') : '<li class="bb-du__combo-msg" role="presentation">No precons match “' + esc(inp.value.trim()) + '”.</li>';
      lb.scrollTop = 0; active = -1;
      if (items.length && inp.value.trim()) setActive(0);
    }
    async function update() {
      var my = ++seq;
      if (!preconIdxP) render('Loading precons…');
      var list = await loadPreconIndex(self.root).catch(function () { return null; });
      if (my !== seq || document.activeElement !== inp) return;
      if (!list) { render('The precon list didn’t load. Paste your deck below instead.'); return; }
      items = preconSearch(list, inp.value);
      render();
    }
    function pick(k) {
      var p = items[k];
      if (!p) return;
      close();
      self.loadPrecon(p);
      var st = self.els.run.closest('.bb-db__actions') || self.els.run;
      if (st && st.scrollIntoView) st.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    self.loadPrecon = function (p) {
      self.runId++; self.busy = false; self.setRunBusy(false);
      self.els.list.value = preconText(p);
      self.els.cmdr.value = ''; self.cmdrOverride = null; self.els.cmdr.placeholder = 'Auto-detected from your list';
      if (self.els.format.value !== 'commander') { self.els.format.value = 'commander'; self.syncFormatUi(); }
      self.markListError(false);
      self.preconLabel = p.name + ' · ' + p.where;
      inp.value = self.preconLabel;
      inp.blur();
      self.saveState();
      self.run();
    };
    inp.addEventListener('focus', function () {
      if (inp.value === self.preconLabel && self.preconLabel) inp.select();
      // Phones: bring the box near the top so the list has room above the keyboard.
      if (window.innerWidth < 750) setTimeout(function () {
        var y = inp.getBoundingClientRect().top;
        if (y > 140 || y < 60) window.scrollBy({ top: y - 90, behavior: 'smooth' });
      }, 250);
      update();
    });
    inp.addEventListener('input', update);
    inp.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!open) { update(); return; }
        setActive(active + (e.key === 'ArrowDown' ? 1 : -1));
      } else if (e.key === 'Enter') {
        if (open && active >= 0) { e.preventDefault(); pick(active); }
      } else if (e.key === 'Escape') {
        if (open) { e.preventDefault(); close(); }
      }
    });
    inp.addEventListener('blur', function () {
      setTimeout(function () { if (document.activeElement !== inp) { close(); inp.value = self.preconLabel || ''; } }, 150);
    });
    // Keep focus in the box while tapping/clicking an option, then pick it on click.
    lb.addEventListener('mousedown', function (e) { e.preventDefault(); });
    lb.addEventListener('click', function (e) {
      var o = e.target.closest('[role=option]');
      if (o) pick(parseInt(o.getAttribute('data-k'), 10));
    });
  };

  /* ---- Saved state (localStorage, versioned; silently skipped if storage is blocked) ---- */
  var STORE_KEY = 'bb-du:state:v1';
  function storeGet() { try { var v = window.localStorage.getItem(STORE_KEY); return v ? JSON.parse(v) : null; } catch (e) { return null; } }
  function storeSet(o) { try { window.localStorage.setItem(STORE_KEY, JSON.stringify(o)); return true; } catch (e) { return false; } }
  function storeDel() { try { window.localStorage.removeItem(STORE_KEY); } catch (e) {} }
  Upgrader.prototype.saveState = function () {
    var r = $('[data-bb-du-budget]:checked', this.root);
    var list = this.els.list.value;
    if (!list.trim()) { storeDel(); return; }
    storeSet({
      v: 1, savedAt: Date.now(), list: list.slice(0, 20000), format: this.els.format.value,
      budget: r ? r.value : '25', custom: this.els.custom.value, max: this.els.max.value,
      conds: $all('[data-bb-du-condition]', this.root).filter(function (e) { return e.checked; }).map(function (e) { return e.value; }),
      finish: this.els.finish.value, cuts: this.els.cuts ? !!this.els.cuts.checked : null,
      cmdr: this.els.cmdr.value, cmdrOverride: this.cmdrOverride || null,
      bracket: this.els.bracket ? this.els.bracket.value : null,
      precon: this.preconLabel || ''
    });
  };
  Upgrader.prototype.restoreState = function () {
    var s = storeGet();
    if (!s || s.v !== 1 || typeof s.list !== 'string' || !s.list.trim()) return false;
    if (this.els.list.value.trim()) return false; // never overwrite something already typed
    function setSel(sel, v) { if (sel && v != null && $all('option', sel).some(function (o) { return o.value === String(v); })) sel.value = String(v); }
    this.els.list.value = s.list;
    setSel(this.els.format, s.format);
    setSel(this.els.max, s.max);
    setSel(this.els.finish, s.finish);
    setSel(this.els.bracket, s.bracket);
    var b = $('[data-bb-du-budget][value="' + String(s.budget).replace(/[^\w]/g, '') + '"]', this.root);
    if (b) b.checked = true;
    if (s.custom != null) this.els.custom.value = s.custom;
    if (Array.isArray(s.conds) && s.conds.length) $all('[data-bb-du-condition]', this.root).forEach(function (c) { c.checked = s.conds.indexOf(c.value) !== -1; });
    if (this.els.cuts && typeof s.cuts === 'boolean') this.els.cuts.checked = s.cuts;
    if (typeof s.cmdr === 'string') this.els.cmdr.value = s.cmdr;
    this.cmdrOverride = typeof s.cmdrOverride === 'string' ? s.cmdrOverride : null;
    if (typeof s.precon === 'string') this.setPreconLabel(s.precon);
    return true;
  };
  Upgrader.prototype.showResume = function (on) {
    if (this.els.resume) this.els.resume.hidden = !on;
  };
  Upgrader.prototype.syncHeadCart = async function (cart) {
    if (!this.els.headCart) return;
    if (!cart) { try { cart = await fetchJson(rootPath() + 'cart.js', { credentials: 'same-origin' }); } catch (e) { cart = null; } }
    var n = (cart && cart.item_count) || 0;
    if (this.els.headCartN) this.els.headCartN.textContent = n ? ' (' + n + ')' : '';
    this.els.headCart.setAttribute('aria-label', n ? 'View cart, ' + n + ' item' + (n === 1 ? '' : 's') : 'View cart');
  };

  Upgrader.prototype.syncChips = function () {
    $all('.bb-db__chip', this.root).forEach(function (l) {
      var i = l.querySelector('input[type=checkbox], input[type=radio]');
      if (i) l.classList.toggle('is-on', i.checked);
    });
  };
  Upgrader.prototype.syncFormatUi = function () {
    if (this.els.cmdrWrap) this.els.cmdrWrap.hidden = this.els.format.value !== 'commander';
    if (this.els.bracketWrap) this.els.bracketWrap.hidden = this.els.format.value !== 'commander';
  };
  Upgrader.prototype.setStatus = function (msg, kind) {
    this.els.status.textContent = msg || '';
    this.els.status.classList.toggle('is-busy', kind === 'busy');
    this.els.status.classList.toggle('is-error', kind === 'error');
    this.els.status.classList.toggle('is-ok', kind === 'ok');
    this.els.status.classList.toggle('is-shown', !!msg);
    if (kind === 'busy' && msg) {
      var ld = $('.bb-du__loading', this.els.results);
      if (ld) ld.innerHTML = '<span class="bb-du__spinner" aria-hidden="true"></span>' + esc(msg);
    }
    if (this.els.barMsg) {
      var show = !!msg && (kind === 'ok' || kind === 'error') && this.ctx && !/^Done/.test(msg);
      this.els.barMsg.textContent = show ? msg : '';
      this.els.barMsg.className = 'bb-du__barmsg' + (kind === 'error' ? ' is-error' : '');
    }
  };
  Upgrader.prototype.markListError = function (on) {
    this.els.list.classList.toggle('is-invalid', !!on);
    if (on) this.els.list.setAttribute('aria-invalid', 'true'); else this.els.list.removeAttribute('aria-invalid');
  };
  Upgrader.prototype.setRunBusy = function (on) {
    this.els.run.disabled = !!on;
    this.els.run.classList.toggle('is-busy', !!on);
    this.els.run.innerHTML = on ? '<span class="bb-du__spinner" aria-hidden="true"></span> Finding upgrades…' : esc(this.runLabel || 'Find upgrades');
    this.root.setAttribute('aria-busy', on ? 'true' : 'false');
  };
  Upgrader.prototype.setProgress = function (done, total) {
    if (!total) { this.els.progress.classList.remove('is-on'); this.els.bar.style.width = '0%'; return; }
    this.els.progress.classList.add('is-on');
    this.els.bar.style.width = Math.min(100, Math.round((done / total) * 100)) + '%';
  };

  /* Build ranked candidates. */
  Upgrader.prototype.run = async function () {
    if (this.busy) return;
    this.showResume(false);
    var st = this.state();
    if (!this.els.list.value.trim() && this.dbList && this.dbList.value.trim()) {
      this.els.list.value = this.dbList.value; // nothing pasted here yet: use the Deck Builder's list
      this.cmdrOverride = null;
    }
    var entries = parseDeck(this.els.list.value);
    if (!entries.length) {
      this.setStatus('Paste your deck list first' + (this.dbList ? ', or use the list from the Deck Builder above.' : '.'), 'error');
      this.markListError(true); this.els.list.focus();
      return;
    }
    if (entries.every(function (e) { return /^(?:https?:\/\/|www\.)\S+$/i.test(e.name); })) {
      this.setStatus('That looks like a link. We can’t open deck sites from here, so use Export → Copy as text (Moxfield, Archidekt) and paste the list itself.', 'error');
      this.markListError(true); this.els.list.focus();
      return;
    }
    if (!st.budget) { this.setStatus('Pick a budget, or type an amount in Custom.', 'error'); if (this.els.custom) this.els.custom.focus(); return; }
    this.markListError(false);
    this.busy = true;
    var runId = ++this.runId;
    this.setRunBusy(true);
    this.els.results.hidden = false;
    this.els.summary.innerHTML = ''; this.els.outside.innerHTML = ''; this.els.cutlist.innerHTML = '';
    this.els.picks.innerHTML = '<div class="bb-du__loading" role="status"><span class="bb-du__spinner" aria-hidden="true"></span>Reading your deck…</div>';
    this.els.results.classList.add('is-loading');
    this.excluded = Object.create(null);
    this.added = Object.create(null);
    this.qty = Object.create(null);
    this.addedQty = Object.create(null);
    this.statsOff = Object.create(null);
    this.moreOpen = false;
    this.roleOn = Object.create(null);
    var self0 = this;
    if (!this.roleIdx) loadRoleIndex(this.root.getAttribute('data-roles-url')).then(function (ix) { self0.roleIdx = ix; if (self0.fit && !self0.els.results.classList.contains('is-loading')) { self0.applyRoles(); self0.renderStats(); } });
    var notices = [];
    var bdP = st.format === 'commander' ? loadBracketData(this.root.getAttribute('data-combos-url')).catch(function () { return null; }) : null;
    var precP = st.format === 'commander' ? loadPrecons(this.root).catch(function () { return null; }) : null;
    try {
      this.setStatus('Reading your deck…', 'busy');
      this.setProgress(1, 10);
      var deckCards = await scryfallCollection(entries.map(function (e) { return e.name; }));
      var deckKeys = Object.create(null);
      entries.forEach(function (e) { deckKeys[frontKey(e.name)] = true; });
      Object.keys(deckCards).forEach(function (k) { deckKeys[frontKey(deckCards[k].name)] = true; });
      var unknown = entries.filter(function (e) { return !deckCards[frontKey(e.name)] && !deckCards[nameKey(e.name)]; });
      if (unknown.length === entries.length) throw new Error('We couldn’t find any Magic cards in that list. Paste one card per line, like “1 Sol Ring”.');
      if (unknown.length) notices.push('We couldn’t identify ' + unknown.length + ' line' + (unknown.length > 1 ? 's' : '') + ': ' + unknown.slice(0, 5).map(function (e) { return e.name; }).join(', ') + (unknown.length > 5 ? '…' : '') + '.');
      function cardOf(e) { return deckCards[frontKey(e.name)] || deckCards[nameKey(e.name)]; }
      var al = deckCards._aliases || [];
      // Cards matched under an alternate/flavor name: listed (not explained) in the results, only when there are any.
      var aliasSeen = Object.create(null);
      al = al.filter(function (a) { var k = nameKey(a.from); if (aliasSeen[k]) return false; aliasSeen[k] = true; return true; });

      var fmt = st.format;
      var commanders = [];
      var cmdrInfo = null;
      var colors;
      if (fmt === 'commander') {
        var typed = ws(this.els.cmdr.value);
        if (typed) {
          var typedNames = typed.split(/\s*(?:\+|;|\s&\s)\s*/).filter(Boolean);
          var tc = await scryfallCollection(typedNames);
          typedNames.forEach(function (n) { var c = tc[frontKey(n)] || tc[nameKey(n)]; if (c) commanders.push(c); });
          if (!commanders.length) throw new Error('We couldn’t find a commander named “' + typed + '”. Check the spelling.');
        } else {
          var det = detectCommanders(entries, cardOf);
          cmdrInfo = det;
          if (this.cmdrOverride) {
            var ov = this.cmdrOverride.split(' + ');
            var ovCards = [];
            ov.forEach(function (n) { var c = det.candidates.filter(function (x) { return x.name === n; })[0]; if (c) ovCards.push(c); });
            if (ovCards.length) { commanders = ovCards; det.how = 'picked'; }
          }
          if (!commanders.length) commanders = det.cards.slice();
          if (!commanders.length) throw new Error('Tell us your commander: add a “Commander” header to your list or type it in the Commander box.');
          this.els.cmdr.placeholder = commanders.map(function (c) { return c.name; }).join(' + ');
        }
        commanders.forEach(function (c) { deckKeys[frontKey(c.name)] = true; });
        colors = [];
        commanders.forEach(function (c) { (c.color_identity || []).forEach(function (x) { if (colors.indexOf(x) === -1) colors.push(x); }); });
      } else {
        colors = [];
        entries.forEach(function (e) { var c = cardOf(e); if (c) (c.color_identity || []).forEach(function (x) { if (colors.indexOf(x) === -1) colors.push(x); }); });
      }
      var colorStr = colors.length ? 'WUBRG'.split('').filter(function (x) { return colors.indexOf(x) !== -1; }).join('') : 'C';

      // Deck cards not legal in the chosen format.
      var illegal = [];
      entries.forEach(function (e) { var c = cardOf(e); if (c && !legalIn(c, fmt) && illegal.indexOf(c.name) === -1) illegal.push(c.name); });

      // Bracket check of the pasted deck (Commander only).
      var bd = null, analysis = null, deckMap = Object.create(null), cmdrKeys = Object.create(null);
      if (fmt === 'commander') {
        entries.forEach(function (e) { if (e.section === 'sideboard' || e.section === 'companion') return; var c = cardOf(e); if (c) deckMap[frontKey(c.name)] = c; });
        commanders.forEach(function (c) { deckMap[frontKey(c.name)] = c; cmdrKeys[frontKey(c.name)] = true; });
        this.setStatus('Checking your deck’s bracket…', 'busy');
        bd = await bdP;
        if (bd && bd.ok) analysis = analyzeDeck(bd, deckMap, cmdrKeys);
        else notices.push('We couldn’t load the bracket data right now, so bracket limits aren’t applied to these picks.');
      }

      this.setStatus('Finding popular upgrades…', 'busy');
      this.setProgress(3, 10);
      var cands = [];
      var edh = null;
      var source = '';
      if (fmt === 'commander') {
        edh = await this.loadEdhrec(commanders).catch(function () { return null; });
        if (edh && edh.list.length) {
          source = 'EDHREC';
          cands = edh.list;
        } else {
          notices.push('EDHREC didn’t answer, so these picks use Scryfall popularity instead of commander-specific data.');
        }
      }
      if (!cands.length) {
        cands = await this.scryfallCandidates(fmt, colorStr, entries.map(cardOf).filter(Boolean));
        source = source || (fmt === 'commander' ? 'Scryfall' : 'Scryfall + your deck’s themes');
      }
      if (runId !== this.runId) return;

      // Remove what they already run, basics, and commanders.
      var seenK = Object.create(null);
      cands = cands.filter(function (c) {
        var k = frontKey(c.name);
        if (deckKeys[k] || seenK[k] || BASIC_RE.test(c.name)) return false;
        seenK[k] = true;
        return true;
      });
      // Precon decks: the precon cards they left out were usually cut on purpose, but EDHREC rates them
      // highly (most decks for a precon commander are close to the precon). Skip that precon-specific filler
      // when the list was built from the precon; tone it down for a precon commander built from scratch.
      // Staples (isStaple) are never affected, so a missing Arcane Signet still comes back.
      var preconCut = Object.create(null), precon = null, pdecks = null;
      if (fmt === 'commander' && source === 'EDHREC') {
        pdecks = await precP;
        var ck0 = Object.create(null);
        commanders.forEach(function (c) { ck0[frontKey(c.name)] = true; });
        var pm = matchPrecon(pdecks, ck0, deckKeys);
        if (pm && (pm.overlap >= 30 || pm.face)) {
          precon = { name: pm.deck.name, overlap: pm.overlap, built: pm.overlap >= 30, skipped: [], toned: 0, staplesKept: [] };
          Object.keys(pm.deck.cards).forEach(function (k) { if (!deckKeys[k]) preconCut[k] = true; });
        }
      }
      function preconFiller(k, card) { return !!preconCut[k] && precon && precon.built && !isStaple(pdecks, k, card); }
      cands.sort(function (a, b) { return b.score - a.score; });

      // Verify legality + identity with Scryfall for EDHREC candidates.
      if (source === 'EDHREC') {
        this.setStatus('Checking legality…', 'busy');
        this.setProgress(4, 10);
        cands = cands.slice(0, CAND_POOL);
        var sf = await scryfallCollection(cands.map(function (c) { return c.name; }), { resolve: false });
        cands = cands.filter(function (c) {
          var card = sf[frontKey(c.name)] || sf[nameKey(c.name)];
          if (!card) return true; // trust EDHREC if Scryfall missed it
          c.card = card;
          return legalIn(card, 'commander') && identityOk(card, colors);
        });
        if (precon) cands = cands.filter(function (c) {
          var k = frontKey(c.name);
          if (!preconCut[k]) return true;
          if (isStaple(pdecks, k, c.card)) { precon.staplesKept.push(c.name); return true; }
          if (precon.built) { precon.skipped.push(c.name); return false; }
          c.score *= 0.6; precon.toned++;
          return true;
        });
        // Big budgets: lift proven, high-impact staples (Ancient Tomb, fetch lands, Mana Vault…).
        // EDHREC play rates undercount pricey cards because most players can't afford them, so with
        // a large budget a well-played expensive card outranks a slightly more popular $1 card.
        // (Applied in applyBudgetRank so changing the budget later re-ranks too.)
        cands.forEach(function (c) {
          var info = edh.byKey[frontKey(c.name)];
          var usd = c.card && c.card.prices ? parseFloat(c.card.prices.usd || c.card.prices.usd_foil || '0') : 0;
          c.premW = (!info || !usd || (info.inclusion < 0.1 && info.synergy < 0.1)) ? 0 : Math.max(0, Math.min(1, Math.log(usd / 2) / Math.log(50)));
        });
      } else {
        // Belt and braces: every 60-card suggestion must be legal in the chosen format.
        cands = cands.filter(function (c) { return c.card && legalIn(c.card, fmt) && identityOk(c.card, colors.length ? colors : []); });
      }
      // Bracket "build toward" pool: Game Changers, fast mana, free and cheap interaction, efficient tutors and
      // cards that complete a two-card combo with something already in the deck, even if EDHREC
      // doesn't list them for this commander. Their rank only goes up when the target bracket asks for it.
      if (fmt === 'commander' && analysis) {
        this.setStatus('Looking for bracket upgrades…', 'busy');
        var have = Object.create(null);
        cands.forEach(function (c) { have[frontKey(c.name)] = true; });
        var extra = [], extraSeen = Object.create(null);
        function addExtra(n) { var k = frontKey(n); if (have[k] || extraSeen[k] || deckKeys[k] || BASIC_RE.test(n)) return; extraSeen[k] = true; extra.push(n); }
        var preCards = Object.create(null);
        (bd.gcCards || []).forEach(function (c) { preCards[frontKey(c.name)] = c; addExtra(c.name); });
        Object.keys(bd.gc).forEach(function (k) { addExtra(bd.gc[k]); });
        var comboNames = [];
        Object.keys(deckMap).forEach(function (k) {
          (bd.combos[k] || []).forEach(function (rec) {
            var other = rec.ka === k ? rec.b : rec.a;
            if (rec.cmdr && !cmdrKeys[rec.cmdr]) return;
            comboNames.push(other);
          });
        });
        // Also partners of our own top suggestions, so two picks can form a combo (Archangel of Thune + Walking Ballista).
        cands.slice(0, 150).forEach(function (c0) {
          var k0 = frontKey(c0.name);
          (bd.combos[k0] || []).forEach(function (rec) {
            if (rec.cmdr && !cmdrKeys[rec.cmdr]) return;
            comboNames.push(rec.ka === k0 ? rec.b : rec.a);
          });
        });
        comboNames.slice(0, 600).forEach(addExtra);
        var hc = [];
        [FAST_MANA, FREE_INTERACTION, CHEAP_INTERACTION, TUTOR_EXTRA].forEach(function (set) { Object.keys(set).forEach(function (k) { if (!have[k] && !extraSeen[k] && !deckKeys[k]) { extraSeen[k] = true; hc.push(set[k]); } }); });
        var need = extra.filter(function (n) { return !preCards[frontKey(n)]; }).concat(hc);
        var looked = need.length ? await scryfallCollection(need, { resolve: false }).catch(function () { return {}; }) : {};
        if (runId !== this.runId) return;
        var added2 = Object.create(null);
        extra.concat(hc).forEach(function (n) {
          var card = preCards[frontKey(n)] || looked[frontKey(n)] || looked[nameKey(n)];
          if (!card) return;
          var k = frontKey(card.name);
          if (have[k] || added2[k] || deckKeys[k] || preconFiller(k, card)) return;
          if (!legalIn(card, 'commander') || !identityOk(card, colors)) return;
          added2[k] = true;
          var info = edh && edh.byKey[k];
          cands.push({ name: card.name, card: card, score: info ? info.score : 0, why: info ? 'In ' + pct(info.inclusion) + ' of ' + edh.label + ' decks' : '', premW: 0, extraPool: true });
        });
      }
      if (fmt === 'commander' && analysis) {
        var candMap = Object.create(null);
        cands.forEach(function (c) { c.key = frontKey(c.name); if (c.card) candMap[c.key] = c.card; });
        cands.forEach(function (c) { c.pf = powerFlags(bd, c, deckMap, cmdrKeys, candMap); });
      }

      var colorWords = colorStr === 'C' ? 'colorless' : colorStr.split('').map(function (x) { return COLOR_NAME[x]; }).join('/');
      cands.forEach(function (c) {
        c.role = cardRole(c.card, colorStr);
        if (fmt === 'commander') return;
        var parts = [];
        (c.syn || []).forEach(function (x) { parts.push(x + ' synergy'); });
        if (c.role === 'Dual land for your colors') parts.unshift('Fixes your ' + colorWords + ' mana');
        else if (c.role === 'Mana fixing land') parts.unshift('Helps cast all your colors');
        var cmc = c.card && !isLandCard(c.card) ? c.card.cmc : null;
        if (!parts.length && c.card) parts.push('Efficient ' + (isLandCard(c.card) ? 'land' : /Creature/.test(c.card.type_line) ? 'creature' : 'spell') + ' for ' + colorWords + ' ' + FORMAT_LABEL[fmt]);
        if (cmc != null && cmc <= 2 && parts.length < 3) parts.push('only ' + cmc + ' mana');
        c.why = parts.length ? parts.join(' · ').replace(/^./, function (x) { return x.toUpperCase(); }) : 'Strong ' + FORMAT_LABEL[fmt] + '-legal ' + colorWords + ' card';
      });
      // Mana base lane: premium on-color lands the deck doesn't run, each replacing a basic. Lands from the
      // main pool move here too, so spells and lands never compete for the same slots.
      var basicCounts = Object.create(null), nBasics = 0;
      entries.forEach(function (e) {
        if (e.section === 'sideboard' || e.section === 'companion') return;
        var bt = basicType(e.name);
        if (bt && bt !== 'Wastes') { basicCounts[bt] = (basicCounts[bt] || 0) + e.qty; nBasics += e.qty; }
      });
      var laneCands = [];
      if (nBasics && colors.length) {
        this.setStatus('Looking at your mana base…', 'busy');
        var laneSeen = Object.create(null);
        var laneFrom = [];
        cands.forEach(function (c) { if (c.card && isLandCard(c.card) && !BASIC_RE.test(c.name)) laneFrom.push(c); });
        var laneNeed = [];
        LAND_TIERS.forEach(function (t) { t[1].forEach(function (n) { var k = frontKey(n); if (!deckKeys[k] && !cands.some(function (c) { return c.key === k || frontKey(c.name) === k; })) laneNeed.push(n); }); });
        var laneLook = laneNeed.length ? await scryfallCollection(laneNeed, { resolve: false }).catch(function () { return {}; }) : {};
        if (runId !== this.runId) return;
        laneNeed.forEach(function (n) { var card = laneLook[frontKey(n)] || laneLook[nameKey(n)]; if (card) laneFrom.push({ name: card.name, card: card, score: 0, why: '' }); });
        laneFrom.forEach(function (c) {
          var k = frontKey(c.name), card = c.card;
          if (laneSeen[k] || deckKeys[k] || !card || preconFiller(k, card)) return;
          if (!legalIn(card, fmt) || !identityOk(card, colors)) return;
          if (bd && analysis && isGameChanger(bd, k, card)) return; // Game Changer lands stay with the bracket-aware picks
          var lb = landBasics(card, colors);
          var tier = LAND_TIER[k] != null ? LAND_TIER[k] : LANE_OTHER_TIER;
          if (tier < LANE_OTHER_TIER && LAND_TIER_NAME[tier] !== 'Utility land' && !lb.types.length) return; // off-color fixing
          var info = edh && edh.byKey ? edh.byKey[k] : null;
          if (!laneWorth(card, lb, LAND_TIER_NAME[tier], info, colors.length)) return; // e.g. Reflecting Pool in a mono-color deck
          laneSeen[k] = true;
          var score = info ? info.score : (c.score || 0);
          var whyL = tier < LANE_OTHER_TIER ? LAND_TIER_NAME[tier] : 'Land upgrade';
          if (lb.fetch) whyL += ' — finds ' + lb.all.join(' or ');
          else if (lb.types.length > 1) whyL += ' — taps for ' + lb.types.map(function (b) { return COLOR_NAME[COLOR_OF_BASIC[b]]; }).join(' and ');
          if (info) whyL += ' · in ' + pct(info.inclusion) + ' of ' + edh.label + ' decks';
          laneCands.push({ name: card.name, card: card, key: k, lane: true, tier: tier, nc: colors.length, score: score, base: score, why: whyL, whyBase: whyL,
            basics: lb, role: c.role || cardRole(card, colorStr), checked: false, offers: null, rank: 999 });
        });
        laneCands.sort(function (a, b) { return (a.tier - b.tier) || (b.score - a.score); });
        cands = cands.filter(function (c) { return !laneSeen[frontKey(c.name)]; });
      }
      cands.forEach(function (c, i) { c.rank = i + 1; c.base = c.score; c.whyBase = c.why || ''; c.key = frontKey(c.name); c.checked = false; c.offers = null; });

      // Suggested cuts
      var cuts = [];
      var deckNonLand = entries.filter(function (e) {
        var c = cardOf(e);
        return c && !isLandCard(c) && e.section !== 'commander' && e.section !== 'commanders' && e.section !== 'sideboard' &&
          !commanders.some(function (cm) { return cm.name === c.name; });
      }).map(cardOf);
      var cutNote = '', cutBase = null, cutPool = null, openSlots = 0;
      var illegalSet = Object.create(null);
      illegal.forEach(function (n) { illegalSet[frontKey(n)] = true; });
      // Banned/illegal cards are always the first cut.
      var illegalCuts = illegal.map(function (n) { return { name: n, why: 'not legal in ' + FORMAT_LABEL[fmt] }; });
      if (fmt === 'commander') {
        var scored = [];
        var seenCut = Object.create(null);
        deckNonLand.forEach(function (c) {
          var k = frontKey(c.name);
          if (seenCut[k] || illegalSet[k]) return;
          seenCut[k] = true;
          if (edh && edh.byKey) {
            var info = edh.byKey[k];
            // Precon cards are overrated for a precon commander, so they count less as the card a pick replaces.
            var raw = info ? info.inclusion + 0.6 * Math.max(0, info.synergy) : -1;
            var pin = precon && pm && pm.deck.cards[k] && !isStaple(pdecks, k, c);
            scored.push({ name: c.name, score: raw, base: pin ? raw * 0.6 : raw,
              why: info ? 'only in ' + pct(info.inclusion) + ' of ' + edh.label + ' decks' : 'not a common pick in ' + edh.label + ' decks' });
          } else {
            scored.push({ name: c.name, score: c.edhrec_rank ? -c.edhrec_rank / 1e5 : -1,
              why: c.edhrec_rank ? 'one of the less-played cards in Commander overall' : 'rarely played in Commander' });
          }
        });
        scored.sort(function (a, b) { return a.score - b.score; });
        // What each new pick would replace, weakest first (open slots in a short list count as 0).
        if (edh && edh.byKey) {
          var dsz = entries.filter(function (e) { return e.section !== 'sideboard'; }).reduce(function (n, e) { return n + e.qty; }, 0);
          cutBase = [];
          for (var fs = dsz; fs < 100; fs++) cutBase.push(0);
          scored.map(function (x) { return Math.max(0, x.base != null ? x.base : x.score); }).sort(function (a, b) { return a - b; }).forEach(function (x) { cutBase.push(x); });
        }
        // Cut pool, weakest first. How many show is set per fit (one per upgrade): see cutsForFit.
        cutPool = scored;
        cuts = illegalCuts.concat(scored.filter(function (x) { return x.score < 0.4; }).slice(0, Math.max(0, 5 - illegalCuts.length)).map(function (x) { return { name: x.name, why: x.why }; }));
        var deckSize = entries.filter(function (e) { return e.section !== 'sideboard'; }).reduce(function (n, e) { return n + e.qty; }, 0);
        openSlots = Math.max(0, 100 - deckSize);
        if (deckSize < 100) cutNote = 'Your list has ' + deckSize + ' of 100 cards, so you can add ' + (100 - deckSize) + ' upgrade' + (100 - deckSize === 1 ? '' : 's') + ' without cutting anything.';
        if (!cuts.length) cutNote = (cutNote ? cutNote + ' ' : '') + (deckNonLand.length ? 'Every nonland card in your list is a popular pick' + (edh ? ' for ' + edh.label : '') + ', so we have no obvious cuts.' : 'Add your nonland cards to the list and we’ll point out the weakest ones.');
      } else {
        cuts = illegalCuts;
        if (!cuts.length) cutNote = 'Everything in your list is legal in ' + FORMAT_LABEL[fmt] + '. For 60-card decks we only flag illegal cards, since there’s no free play-rate data to rank the rest.';
      }

      cuts.forEach(function (c) { c.card = deckCards[frontKey(c.name)] || deckCards[nameKey(c.name)] || null; });
      illegalCuts.forEach(function (c) { c.card = deckCards[frontKey(c.name)] || deckCards[nameKey(c.name)] || null; });
      (cutPool || []).forEach(function (c) { c.card = deckCards[frontKey(c.name)] || deckCards[nameKey(c.name)] || null; });
      this.ctx = {
        fmt: fmt, colors: colorStr, commanders: commanders, cands: cands, cuts: cuts, cutNote: cutNote, illegal: illegal,
        notices: notices, aliases: al, source: source, deckCount: entries.reduce(function (n, e) { return n + e.qty; }, 0),
        lookupErrors: 0, edhLabel: edh ? edh.label : '', cmdrInfo: cmdrInfo,
        bd: analysis ? bd : null, analysis: analysis, deckMap: deckMap, cmdrKeys: cmdrKeys,
        laneCands: laneCands, basicCounts: basicCounts, nBasics: nBasics, cutBase: cutBase, precon: precon,
        cutPool: cutPool, illegalCuts: illegalCuts, openSlots: openSlots, deckCards: deckCards,
        deckList: entries.filter(function (e) { return e.section !== 'sideboard' && e.section !== 'companion'; }).map(function (e) { return { name: e.name, qty: e.qty, card: cardOf(e) || null }; })
      };
      if (!cands.length) throw new Error('We couldn’t find upgrade candidates for that deck.');
      await this.fill(runId);
    } catch (err) {
      console.error('[bb-deck-upgrade]', err);
      // Drop the half-built results so a later filter change can't bring back picks for a different list.
      if (runId === this.runId) { this.els.results.hidden = true; this.els.results.classList.remove('is-loading'); this.ctx = null; this.fit = null; }
      this.setStatus((err && err.message && !/^HTTP|fetch|network|abort/i.test(err.message)) ? err.message : 'Something went wrong talking to the card databases. Please try again in a minute.', 'error');
      this.setProgress(0, 0);
    } finally {
      if (runId === this.runId) { this.busy = false; this.setRunBusy(false); }
    }
  };

  Upgrader.prototype.loadEdhrec = async function (commanders) {
    var names = commanders.map(function (c) { return c.name; });
    var slugs = [];
    if (names.length > 1) {
      var s = names.map(edhrecSlug);
      slugs.push(s.slice().sort().join('-'), s.join('-'), s.slice().reverse().join('-'));
    }
    slugs.push(edhrecSlug(names[0]));
    var data = null;
    for (var i = 0; i < slugs.length && !data; i++) {
      var d = await fetchJson(EDHREC + slugs[i] + '.json', null).catch(function () { return null; });
      if (d && d.redirect) d = await fetchJson('https://json.edhrec.com/pages' + d.redirect + '.json', null).catch(function () { return null; });
      if (d && d.container && d.container.json_dict && d.container.json_dict.cardlists) data = d;
    }
    if (!data) return null;
    var label = commanders.map(function (c) { return c.name.split(/\s*\/\/\s*/)[0].split(',')[0]; }).join(' + ');
    var byKey = Object.create(null);
    var list = [];
    data.container.json_dict.cardlists.forEach(function (cl) {
      (cl.cardviews || []).forEach(function (cv) {
        if (!cv.name) return;
        var inc = cv.potential_decks ? (cv.num_decks || 0) / cv.potential_decks : (cv.inclusion || 0) / 100;
        var syn = typeof cv.synergy === 'number' ? cv.synergy : 0;
        var k = frontKey(cv.name);
        var score = inc + 0.6 * Math.max(0, syn);
        if (byKey[k] && byKey[k].score >= score) return;
        var why = 'In ' + pct(inc) + ' of ' + label + ' decks' + (syn >= 0.15 ? ' · high synergy' : '');
        byKey[k] = { inclusion: inc, synergy: syn, score: score };
        list.push({ name: cv.name, score: score, why: why });
      });
    });
    return { list: list, byKey: byKey, label: label };
  };

  Upgrader.prototype.scryfallCandidates = async function (fmt, colorStr, deckCards) {
    var label = FORMAT_LABEL[fmt];
    var base = 'legal:' + fmt + ' id<=' + colorStr + ' -t:basic game:paper';
    var out = [];
    var byName = Object.create(null);
    function add(list, top, spread, why, syn) {
      list.forEach(function (c, i) {
        var s = top - (i / Math.max(1, list.length)) * spread;
        var k = frontKey(c.name);
        if (byName[k]) {
          if (byName[k].score < s) byName[k].score = s;
          else byName[k].score += 0.05; // shows up in more than one list
          if (syn && byName[k].syn.indexOf(syn) === -1) byName[k].syn.push(syn);
          return;
        }
        out.push(byName[k] = { name: c.name, card: c, score: s, why: why, syn: syn ? [syn] : [] });
      });
    }
    if (fmt === 'commander') {
      add(await scryfallSearch(base, 2), 1, 0.9, 'Popular Commander card in your colors');
      return out;
    }
    // 60-card formats: Scryfall has no constructed play data, so lean on the
    // deck itself (tribe, keywords, card types) and skip multiplayer-only cards.
    var constructed = base + ' -o:commander -o:"command zone" -o:"maximum hand size" -o:"each opponent" -o:"each player" -t:equipment';
    var spells = constructed + ' -t:land cmc<=3' + (colorStr !== 'C' ? ' (' + colorStr.split('').map(function (x) { return 'c:' + x; }).join(' OR ') + ')' : '');
    add(await scryfallSearch(spells, 1), 0.9, 0.8, 'Efficient ' + label + '-legal spell in your colors');
    if (colorStr.length >= 2 && colorStr !== 'C') {
      var lands = await scryfallSearch(base + ' t:land -t:basic id=' + colorStr + ' -o:commander', 1).catch(function () { return []; });
      add(lands.slice(0, 20), 0.75, 0.4, 'Mana fixing for ' + colorStr.split('').map(function (x) { return COLOR_NAME[x]; }).join('/'));
    }
    var counts = Object.create(null), kw = Object.create(null), types = Object.create(null);
    deckCards.forEach(function (c) {
      var t = String(c.type_line || '').split('//')[0];
      ['Creature', 'Instant', 'Sorcery', 'Artifact', 'Enchantment', 'Planeswalker'].forEach(function (ty) { if (t.indexOf(ty) !== -1) types[ty] = (types[ty] || 0) + 1; });
      (c.keywords || []).forEach(function (k) { if (!/^(flying|trample|haste|vigilance|reach|first strike|deathtouch|lifelink|scry|mill|surveil|flash|ward)$/i.test(k)) kw[k] = (kw[k] || 0) + 1; });
      if (!/Creature/.test(t) || t.indexOf('—') === -1) return;
      t.split('—')[1].trim().split(/\s+/).forEach(function (sub) { counts[sub] = (counts[sub] || 0) + 1; });
    });
    var top = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; })[0];
    if (top && counts[top] >= (top === 'Human' ? 6 : 4)) {
      var tribal = await scryfallSearch(constructed + ' (t:' + top + ' OR o:' + top + ')', 1).catch(function () { return []; });
      add(tribal, 1.3, 0.9, top + ' synergy for your deck', top);
    }
    var topKw = Object.keys(kw).filter(function (k) { return kw[k] >= 3; }).sort(function (a, b) { return kw[b] - kw[a]; }).slice(0, 2);
    for (var i = 0; i < topKw.length; i++) {
      var kl = await scryfallSearch(constructed + ' (keyword:"' + topKw[i] + '" OR o:"' + topKw[i].toLowerCase() + '")', 1).catch(function () { return []; });
      add(kl, 1.15, 0.8, topKw[i] + ' synergy for your deck', topKw[i]);
    }
    // Lean toward the card types the deck already plays a lot of.
    var total = deckCards.length || 1;
    out.forEach(function (o) {
      var t = String((o.card && o.card.type_line) || '').split('//')[0];
      var share = 0;
      Object.keys(types).forEach(function (ty) { if (t.indexOf(ty) !== -1) share = Math.max(share, types[ty] / total); });
      o.score += share * 0.3;
    });
    return out;
  };

  /* Pure budget fit over checked candidates. */
  Upgrader.prototype.computeFit = function () {
    var st = this.state();
    var ctx = this.ctx;
    var picks = [], outside = [], held = [], remaining = st.budget, needMore = false;
    function finishOk(o) { return st.finish === 'either' || (st.finish === 'foil' ? o.foil : !o.foil); }
    var bd = ctx.bd, br = st.bracket, an = ctx.analysis;
    var counts = { gc: an ? an.gc.length : 0, deckGc: an ? an.gc.length : 0, xt: an ? an.xt.length : 0, early: an ? an.early.length : 0 };
    (ctx.cands || []).forEach(function (c) { if (c.pairWhy) { c.why = c.whyNoPair; c.pairWhy = null; } });
    var picked = [];
    var self = this;
    // One list, strongest first: basic-for-land swaps compete with every other upgrade on the same
    // impact scale (no land goes first just for being a land). Lands still have a sanity cap.
    var left = Object.create(null), fetchNeeds = Object.create(null), nLane = 0;
    Object.keys(ctx.basicCounts || {}).forEach(function (b) { left[b] = ctx.basicCounts[b]; });
    var laneMax = Math.min(8, Math.max(1, Math.ceil(st.max / 3)), Math.max(0, (ctx.nBasics || 0) - 4));
    var laneBudget = Math.round(st.budget * LANE_BUDGET_SHARE);
    var order = mergedOrder(ctx);
    var gate = st.maxAuto && ctx.cutBase, nMain = 0, gated = false, spellsDone = false, landsDone = false, laneNeed = false;
    for (var i = 0; i < order.length; i++) {
      var c = order[i];
      if (this.excluded[c.key]) continue;
      if (remaining < 10 || picks.length >= st.max) break;
      if (spellsDone && landsDone) break;
      if (c.lane) {
        if (landsDone || nLane >= laneMax) continue;
        // Same bar as spells: the card it replaces is a basic, which still counts for LAND_BASE.
        if (gate && (landImpact(c) < MIN_SCORE || landImpact(c) - LAND_BASE < MIN_GAIN)) { landsDone = true; continue; }
        if (!c.checked && laneTooPricey(c, laneBudget)) continue; // e.g. a $600 original dual on a $100 budget
        if (!c.checked) { needMore = true; laneNeed = true; break; }
        if (!c.offers || !c.offers.length) continue;
        // Basic to cut: one the new land fetches or taps for, from the type with the most copies left,
        // keeping at least 2 of each type plus 1 per fetch land that can find it.
        var types = (c.basics.types.length ? c.basics.types : Object.keys(left)).filter(function (b) { return left[b] > 0; });
        types.sort(function (a, b) { return (left[b] - (fetchNeeds[b] || 0)) - (left[a] - (fetchNeeds[a] || 0)); });
        var cut = types.filter(function (b) { return left[b] - 1 >= 2 + (fetchNeeds[b] || 0) + (c.basics.fetch && c.basics.types.indexOf(b) !== -1 ? 1 : 0); })[0];
        if (!cut) continue;
        var inF = c.offers.filter(function (o) { return st.conds.indexOf(o.cond) !== -1 && finishOk(o); });
        var off = inF[0], note = '';
        if (!off) {
          // Lands: show an in-stock copy in another condition with a note, but never the wrong finish
          // (Nonfoil only / Foil only are hard filters for the picks).
          off = c.offers.filter(finishOk).sort(function (a, b) { return a.price - b.price; })[0];
          if (!off) continue;
          note = 'Only ' + COND_LABEL[off.cond] + ' in stock';
        }
        if (off.price > remaining || off.price > laneBudget) continue;
        remaining -= off.price; laneBudget -= off.price; nLane++;
        left[cut]--;
        if (c.basics.fetch) c.basics.types.forEach(function (b) { fetchNeeds[b] = (fetchNeeds[b] || 0) + 1; });
        c.isGC = false; c.upTags = [];
        picks.push({ cand: c, offer: off, lane: true, cut: cut, note: note });
        picked.push({ key: c.key, card: c.card });
        continue;
      }
      if (spellsDone) continue;
      // Auto: stop once a card no longer clearly beats the weakest card it would replace, so a small
      // budget gets a couple of strong picks instead of filler.
      // Building toward 4/5 and not there yet: Game Changers (non-land, or EDHREC-listed for this commander) skip the Auto quality bar.
      var needB4 = ctx.push >= 4 && counts.gc <= MAX_GC_B3 && !counts.early;
      var gcNeed = needB4 && c.pf && c.pf.gc && (c.whyBase || !(c.card && isLandCard(c.card)));
      // Combo with another suggestion counts only when that partner is already a pick (so it's in stock).
      var pairOn = c.pairAdd > 0 && picked.some(function (x) { return c.pf.pairs.some(function (pp) { return pp.key === x.key; }); });
      var sEff = c.score + (pairOn ? c.pairAdd : 0);
      if (gate && !gcNeed && (sEff < MIN_SCORE || sEff - (ctx.cutBase[nMain] || 0) < MIN_GAIN)) { gated = true; continue; } // near-ties can be out of strict order
      if (!c.checked) { needMore = true; break; }
      if (!c.offers || !c.offers.length) continue;
      // Never push the deck past the chosen bracket (checks deck cards + picks made so far).
      var why = bd && br ? bracketBlock(bd, br, c, ctx.deckMap, ctx.cmdrKeys, picked, counts) : '';
      if (why) { held.push({ cand: c, reason: why }); continue; }
      var ok = c.offers.filter(function (o) { return st.conds.indexOf(o.cond) !== -1 && finishOk(o); });
      // Only stocked in the other finish: never a pick. It can still show under "outside your filters".
      var inlineNote = '';
      if (ok.length && ok[0].price <= remaining) {
        picks.push({ cand: c, offer: ok[0], note: inlineNote }); remaining -= ok[0].price; nMain++;
        if (pairOn && !c.pairWhy) {
          var pp0 = c.pf.pairs.filter(function (pp) { return picked.some(function (x) { return x.key === pp.key; }); })[0];
          c.pairWhy = 'Combos with ' + pp0.with + ' (also picked)' + (pp0.mv != null ? ', ' + pp0.mv + ' mana' : '') + ': ' + String(pp0.result || '').toLowerCase();
          c.whyNoPair = c.why;
          c.why = c.whyBase ? c.whyBase + ' · ' + c.pairWhy : c.pairWhy;
        }
        picked.push({ key: c.key, card: c.card });
        c.isGC = !!(bd && isGameChanger(bd, c.key, c.card));
        if (c.isGC) counts.gc++;
        // What this pick does to the bracket: combos it completes with the deck or earlier picks.
        c.upTags = [];
        if (bd) {
          var ck = c.key;
          (bd.combos[ck] || []).some(function (rec) {
            var other = rec.ka === ck ? rec.kb : rec.ka;
            var oc = ctx.deckMap[other] || (picked.filter(function (x) { return x.key === other; })[0] || {}).card;
            if (!oc || (rec.cmdr && !ctx.cmdrKeys[rec.cmdr])) return false;
            c.upTags.push('Completes combo: ' + c.name.split(' // ')[0] + ' + ' + oc.name.split(' // ')[0]);
            return true;
          });
          if (ctx.push && c.pf) {
            if (c.pf.fast) c.upTags.push('Fast mana');
            if (c.pf.tutor) c.upTags.push('Tutor');
            if (c.pf.free) c.upTags.push(c.pf.cheap ? 'Cheap interaction' : 'Free interaction');
          }
        }
        picked[picked.length - 1].upTags = c.upTags;
        if (c.upTags.some(function (t0) { return /^Completes combo/.test(t0); }) && bd) {
          var ea = analyzeDeck(bd, (function () { var m = Object.create(null); Object.keys(ctx.deckMap).forEach(function (k) { m[k] = ctx.deckMap[k]; }); picked.forEach(function (x) { if (x.card) m[x.key] = x.card; }); return m; })(), ctx.cmdrKeys);
          counts.early = ea.early.length;
        }
        if (bd && bd.xt[c.key]) counts.xt++;
        continue;
      }
      // Not available within the filters: is there an in-stock copy outside them that fits the budget?
      // (Also offered when the in-filter copy is too pricey but another condition/finish fits.)
      // Only offer it if it fits what's left at this rank, so allowing it always turns it into a pick.
      var alt = c.offers.filter(function (o) { return o.price <= remaining && ok.indexOf(o) === -1; })[0];
      if (alt && outside.length < MAX_OUTSIDE) {
        var reasons = [];
        if (st.conds.indexOf(alt.cond) === -1) reasons.push(COND_LABEL[alt.cond]);
        if (!finishOk(alt)) reasons.push(alt.foil ? 'foil' : 'non-foil');
        outside.push({ cand: c, offer: alt, reason: reasons.join(' + ') });
      }
    }
    // 3) More upgrades worth a look: the next-strongest in-stock cards that didn't make the cut, by impact.
    //    Auto: only strong cards you can't take as-is: over the budget that's left, or outside the filters
    //    (e.g. foil-only with Nonfoil only). Weak fits that fit the budget aren't upgrades, so they don't show.
    //    A set pick count: also the next cards past that count.
    var nMainF = picks.filter(function (p) { return !p.lane; }).length;
    var more = [], pickedK = Object.create(null), heldK = Object.create(null), outK = Object.create(null);
    picks.forEach(function (p) { pickedK[p.cand.key] = true; });
    held.forEach(function (h) { heldK[h.cand.key] = true; });
    outside.forEach(function (o) { outK[o.cand.key] = true; });
    var floor = ctx.cutBase ? MIN_SCORE : -1e9, moreUnchecked = false;
    var pool = mergedOrder(ctx).filter(function (c) { return !c.lane || ((c.tier < LANE_OTHER_TIER || c.score >= MIN_SCORE) && !laneTooPricey(c, st.budget)); });
    pool.forEach(function (c) {
      if (more.length >= MORE_MAX * 2 || pickedK[c.key] || heldK[c.key] || outK[c.key] || self.excluded[c.key]) return;
      if (!c.lane && c.score < floor) return;
      // Auto: the same quality bar as the picks (clearly beats the card it would replace).
      if (st.maxAuto && ctx.cutBase && (c.lane ? landImpact(c) < MIN_SCORE || landImpact(c) - LAND_BASE < MIN_GAIN
        : c.score - (ctx.cutBase[nMainF] || 0) < MIN_GAIN)) return;
      if (!c.checked) { moreUnchecked = true; return; }
      if (!c.offers || !c.offers.length) return;
      if (bd && br && bracketBlock(bd, br, c, ctx.deckMap, ctx.cmdrKeys, picked, counts)) return;
      var okM = c.offers.filter(function (o) { return st.conds.indexOf(o.cond) !== -1 && finishOk(o); });
      var noteM = '';
      // Foil-only (or nonfoil-only) stock never makes the main picks, but it can show down here.
      if (!okM.length) { okM = c.offers.filter(function (o) { return st.conds.indexOf(o.cond) !== -1; }); if (okM.length) noteM = okM[0].foil ? 'Foil only in stock' : 'Nonfoil only in stock'; }
      if (!okM.length) return;
      if (st.maxAuto) {
        if (!noteM && okM.some(function (o) { return o.price <= remaining; })) return; // fits: only the mana-base rules kept it out
      }
      more.push({ cand: c, offer: okM[0], note: noteM, more: true });
    });
    // Lands and spells together, strongest first, on the same impact scale as the picks.
    more.sort(function (a, b) { return impactOf(b.cand) - impactOf(a.cand); });
    more = more.slice(0, MORE_MAX);
    var laneUnchecked = (ctx.laneCands || []).some(function (c) { return !c.checked && !self.excluded[c.key] && !laneTooPricey(c, Math.round(st.budget * LANE_BUDGET_SHARE)); });
    var checkedAll = ctx.cands.every(function (c) { return c.checked; }) && !laneUnchecked;
    var nChecked = ctx.cands.filter(function (c) { return c.checked; }).length;
    var projected = null;
    if (an && bd && picks.length) {
      var merged = Object.create(null);
      Object.keys(ctx.deckMap).forEach(function (k) { merged[k] = ctx.deckMap[k]; });
      picks.forEach(function (p) { if (p.cand.card) merged[p.cand.key] = p.cand.card; });
      projected = analyzeDeck(bd, merged, ctx.cmdrKeys);
    }
    return { picks: picks, outside: outside, held: held, projected: projected, spent: st.budget - remaining, gated: gated, more: more,
      moreNeed: more.length < MORE_MAX && moreUnchecked && ctx.cands.filter(function (c) { return c.checked; }).length < MAX_CHECKS, budget: st.budget, laneNeed: laneNeed && laneUnchecked, needMore: (laneNeed && laneUnchecked) || (needMore && !laneNeed && !checkedAll && nChecked < MAX_CHECKS), state: st };
  };

  // Land swaps on the spell impact scale (inclusion + 0.6 × synergy for EDHREC; 0–1 otherwise). A basic
  // it replaces counts as 0. Lands with little play-rate data get a modest floor by how good the land type is.
  var LAND_PRIOR = { 'Fetch land': 0.4, 'Original dual': 0.45, 'Shock land': 0.4, 'Triome': 0.4, 'Utility land': 0.35,
    'Horizon land': 0.3, 'Fast land': 0.3, 'Check land': 0.28, 'Pain land': 0.28, 'Dual land': 0.2 };
  var LAND_BASE = 0.2; // a basic land is a working card, not an empty slot
  function landImpact(c) {
    var prior = LAND_PRIOR[LAND_TIER_NAME[c.tier]] || 0;
    if (c.basics && c.basics.types && c.basics.types.length < 2 && !c.basics.fetch) prior *= 0.6; // fixes only one color
    else if (c.nc >= 4) prior *= 1.4; else if (c.nc === 3) prior *= 1.2; // fixing matters more in 3+ colors
    return (c.imp = Math.max(c.score || 0, prior));
  }
  function impactOf(c) { return c.lane ? landImpact(c) : c.score; }
  // Spells (already ranked) and lands merged by impact; ties keep spells first.
  function mergedOrder(ctx) {
    var lanes = (ctx.laneCands || []).slice().sort(function (a, b) { return (landImpact(b) - landImpact(a)) || (a.tier - b.tier); });
    var out = [], i = 0, j = 0, sp = ctx.cands;
    while (i < sp.length || j < lanes.length) {
      if (j >= lanes.length || (i < sp.length && sp[i].score >= landImpact(lanes[j]))) out.push(sp[i++]);
      else out.push(lanes[j++]);
    }
    // Near-ties go to the cheaper card: within a band of cards scoring at least TIE_BAND of the band's
    // best, cheapest first, so the budget stretches to more picks.
    var res = [];
    for (var b = 0; b < out.length;) {
      var top = impactOf(out[b]), e = b + 1;
      while (e < out.length && impactOf(out[e]) >= top * TIE_BAND) e++;
      res = res.concat(out.slice(b, e).map(function (c, k) { return { c: c, k: k, p: estCents(c) }; })
        .sort(function (x, y) { return (x.p - y.p) || (x.k - y.k); }).map(function (x) { return x.c; }));
      b = e;
    }
    return res;
  }
  var TIE_BAND = 0.9; // within 10% of the band's top impact counts as a tie
  // Our cheapest in-stock copy once checked, else Scryfall's price as an estimate (unknown = last).
  function estCents(c) {
    if (c.checked && c.offers && c.offers.length) return Math.min.apply(null, c.offers.map(function (o) { return o.price; }));
    return sfCents(c.card) || 1e9;
  }
  // Scryfall's price is close to ours, so a land costing 1.5x the whole lane budget can be skipped unchecked.
  function laneTooPricey(c, laneBudgetCents) { var p = sfCents(c.card); return !!p && p > laneBudgetCents * 1.5; }

  /* Budget-aware ranking: from $50 up, proven staples get a lift that grows with their price
     (≈$2 → none, $100+ → full) and with the budget (full at $300+). */
  function applyBudgetRank(ctx, budgetCents, target) {
    var push = pushMode(target, ctx.analysis);
    var rk = budgetCents + '|' + (push ? target : 0);
    if (ctx.rankKey === rk) return;
    ctx.rankKey = rk;
    ctx.push = push ? target : 0;
    var bw = Math.max(0, Math.min(1, (budgetCents / 100 - 50) / 250));
    ctx.cands.forEach(function (c, i) {
      var pb = push ? powerBoost(c, target) : { add: 0, why: '' };
      c.score = c.base + 0.35 * bw * (c.premW || 0) + pb.add; c.ord = i;
      c.pairAdd = push ? pairBoost(c, target) : 0; c.pairWhy = null;
      c.gcWhy = push && target ? 'Raises your deck toward bracket ' + target : '';
      c.pwhy = pb.why;
      c.why = pb.why ? (c.whyBase ? c.whyBase + ' · ' + pb.why : pb.why) : (c.whyBase || 'Strong Commander card in your colors');
    });
    ctx.cands.sort(function (a, b) { return (b.score - a.score) || (a.ord - b.ord); });
    ctx.cands.forEach(function (c, i) { c.rank = i + 1; });
  }

  /* Check stock in rank order until the budget/max is filled. */
  Upgrader.prototype.fill = async function (runId) {
    var self = this;
    var ctx = this.ctx;
    var st0 = this.state();
    applyBudgetRank(ctx, st0.budget, st0.bracket);
    var fit = this.computeFit(), moreChunks = 0;
    while ((fit.needMore || (fit.moreNeed && moreChunks++ < MORE_CHUNKS)) && runId === this.runId) {
      var lb0 = Math.round(this.state().budget * LANE_BUDGET_SHARE);
      var next = fit.laneNeed ? (ctx.laneCands || []).filter(function (c) { return !c.checked && !self.excluded[c.key] && !laneTooPricey(c, lb0); }).sort(function (a, b) { return landImpact(b) - landImpact(a); }).slice(0, CHECK_CHUNK) : [];
      if (!next.length) next = mergedOrder(ctx).filter(function (c) { return !c.checked && !self.excluded[c.key] && !(c.lane && laneTooPricey(c, fit.moreNeed && !fit.needMore ? self.state().budget : lb0)); }).slice(0, CHECK_CHUNK);
      if (!next.length) break;
      var done = ctx.cands.filter(function (c) { return c.checked; }).length;
      this.setStatus('Checking our stock… (' + done + ' cards checked)', 'busy');
      this.setProgress(4 + Math.min(5.5, done / 15), 10);
      await mapPool(next, STORE_CONCURRENCY, async function (c) {
        try { c.offers = await storeOffers(c.name); }
        catch (e) { c.offers = []; ctx.lookupErrors++; }
        c.checked = true;
      });
      if (runId !== this.runId) return; // a newer run (settings changed) owns the results now
      fit = this.computeFit();
      this.render(fit, true);
    }
    if (runId !== this.runId) return;
    // Picks that are already in the cart (e.g. from an earlier run) show as "In cart ✓" right away.
    var inCartNow = await cartVariantQty().catch(function () { return null; });
    if (runId !== this.runId) return;
    if (inCartNow) fit.picks.concat(fit.more || []).forEach(function (p) {
      if (inCartNow.byVariant[p.offer.variantId] || inCartNow.byHandle[p.offer.handle]) self.added[p.offer.variantId] = true;
    });
    this.render(fit, false);
    this.setProgress(0, 0);
    this.saveState();
    if (this.scrollOnDone !== false) {
      var top = this.els.results.getBoundingClientRect().top;
      if (top > window.innerHeight * 0.6 || top < 0) {
        if (typeof this.els.results.scrollIntoView === 'function') {
          try { this.els.results.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { try { this.els.results.scrollIntoView(); } catch (e2) {} }
        }
      }
    }
    this.scrollOnDone = true;
    var n = fit.picks.length;
    var strong = fit.gated && fit.budget - fit.spent > fit.budget * 0.25;
    this.setStatus(n ? 'Done — ' + n + (strong ? ' strong' : '') + ' upgrade' + (n === 1 ? '' : 's') + ' for ' + money(fit.spent) + ' of your ' + money(fit.budget) + ' budget.' :
      'Done — nothing in stock fits those filters yet. Try a bigger budget or more conditions.', n ? 'ok' : 'error');
  };

  Upgrader.prototype.refit = async function () {
    if (!this.ctx) return;
    var runId = ++this.runId;
    this.scrollOnDone = false;
    try { await this.fill(runId); } catch (e) { this.setStatus('Something went wrong checking stock. Please try again.', 'error'); }
  };

  Upgrader.prototype.includeOutside = function (keys) {
    var fit = this.fit;
    if (!fit) return;
    var self = this;
    var items = fit.outside.filter(function (o) { return !keys || keys.indexOf(o.cand.key) !== -1; });
    items.forEach(function (o) {
      var box = $('[data-bb-du-condition][value="' + o.offer.cond + '"]', self.root);
      if (box && !box.checked) box.checked = true;
      var st = self.state();
      if (st.finish !== 'either' && ((st.finish === 'foil') !== o.offer.foil)) self.els.finish.value = 'either';
    });
    this.syncChips();
    this.refit();
  };

  function cardHtml(p, mode, added, qo) {
    var c = p.cand, o = p.offer;
    var zero = !!(qo && !added && qo.q === 0);
    var img = o.image || cardImage(c.card);
    var sub = [o.setName + (o.setCode ? ' (' + o.setCode + (o.collector ? ' #' + o.collector : '') + ')' : ''), COND_LABEL[o.cond], o.foil ? 'Foil' : '', o.treatment]
      .filter(Boolean).join(' · ');
    var btns = mode === 'more'
      ? '<button type="button" class="bb-db__btn ' + (added ? 'bb-db__btn--secondary' : 'bb-db__btn--primary') + ' bb-du__small" data-bb-du-act="add" data-key="' + esc(c.key) + '"' + (added ? ' disabled' : '') + '>' + (added ? 'In cart ✓' : 'Add to cart') + '</button>'
      : mode === 'pick'
      ? (qo && !added ? '<label class="bb-db__line-meta bb-du__qtyctl" title="Set to 0 if you already own it">Qty <input class="bb-db__qty" type="number" inputmode="numeric" min="0" max="' + qo.cap + '" step="1" value="' + qo.q + '" data-bb-du-setqty data-key="' + esc(c.key) + '" aria-label="Quantity of ' + esc(c.name) + ' (0 if you already own it)"></label>' : '') +
        '<button type="button" class="bb-db__btn ' + (added ? 'bb-db__btn--secondary' : 'bb-db__btn--primary') + ' bb-du__small" data-bb-du-act="add" data-key="' + esc(c.key) + '"' + (added || zero ? ' disabled' : '') + '>' + (added ? 'In cart ✓' : 'Add to cart') + '</button>' +
        (added ? '' : '<button type="button" class="bb-db__btn bb-db__btn--secondary bb-du__small" data-bb-du-act="remove" data-key="' + esc(c.key) + '" aria-label="Skip ' + esc(c.name) + ' and suggest something else" title="Skip — suggest something else">Skip</button>')
      : '<button type="button" class="bb-db__btn bb-db__btn--secondary bb-du__small" data-bb-du-act="include" data-key="' + esc(c.key) + '">Allow ' + esc(p.reason) + '</button>';
    var tags = '';
    if (mode === 'pick' && !p.lane && c.rank <= 5) tags += '<span class="bb-du__tag bb-du__tag--top" title="One of the 5 strongest suggestions for your deck">Top pick</span>';
    if (c.role) tags += '<span class="bb-du__tag">' + esc(c.role) + '</span>';
    if (c.isGC) tags += '<span class="bb-du__tag bb-du__tag--gc" title="On WotC’s Game Changers list (counts toward Commander brackets)">Game Changer</span>';
    if (mode === 'pick') (c.upTags || []).forEach(function (t) { if (tags.indexOf('>' + esc(t) + '<') !== -1) return; tags += '<span class="bb-du__tag bb-du__tag--up">' + esc(t) + '</span>'; });
    if (added) tags += '<span class="bb-du__tag bb-du__tag--ok">In cart</span>';
    // The Game Changer tag already says it: drop that part of the description (no stats left -> a short reason or nothing).
    var why = c.why || '';
    if (c.isGC) {
      why = why.split(' · ').filter(function (x) { return !/^game changer$/i.test(x.trim()); }).join(' · ').replace(/^./, function (x) { return x.toUpperCase(); });
      if (!why) why = c.gcWhy || '';
    }
    return '<article class="bb-du__card' + (added ? ' is-added' : '') + (zero ? ' is-zero' : '') + '" data-variant="' + esc(o.variantId) + '" data-key="' + esc(c.key) + '">' +
      (img ? '<button type="button" class="bb-du__thumb" data-bb-du-preview="' + mode + '" data-key="' + esc(c.key) + '" aria-label="Preview ' + esc(c.name) + '"><img src="' + esc(img) + '" alt="" loading="lazy" width="64" height="89"><span class="bb-du__zoom" aria-hidden="true">⤢</span></button>' : '<span></span>') +
      '<div class="bb-du__card-body"><p class="bb-du__card-name"><button type="button" class="bb-du__namebtn" data-bb-du-preview="' + mode + '" data-key="' + esc(c.key) + '">' + esc(c.name) + '</button></p>' +
      (tags ? '<div class="bb-du__tags">' + tags + '</div>' : '') +
      (p.lane ? '<span class="bb-du__swap">Cut: ' + esc(p.cut) + ' → Add: ' + esc(c.name) + '</span>' : '') +
      (why ? '<span class="bb-du__why">' + esc(why) + '</span>' : '') +
      '<span class="bb-du__sub">' + esc(sub) + '</span>' +
      (mode === 'pick' && qo ? '<label class="bb-du__incl" title="Uncheck to leave this card out of the Deck stats above"><input type="checkbox" data-bb-du-incl data-key="' + esc(c.key) + '"' + (qo.inc === false ? '' : ' checked') + '> Count in stats</label>' : '') +
      ((mode === 'pick' || mode === 'more') && p.note ? '<span class="bb-db__badge bb-db__badge--warn bb-du__note">' + esc(p.note) + '</span>' : '') +
      (mode === 'outside' ? '<span class="bb-db__badge bb-db__badge--warn">Only in stock as ' + esc(p.reason) + '</span>' : '') +
      '<div class="bb-du__row"><span class="bb-du__price">' + money(o.price) + ' <span class="bb-du__qty" data-bb-du-qty="' + esc(o.variantId) + '" data-handle="' + esc(o.handle) + '"></span></span><span class="bb-du__btns">' + btns + '</span></div></div></article>';
  }

  /* One cut per upgrade shown above (Commander). Illegal cards always first; land swaps cut the basic named on
     their card; open slots in a short list need no cut. Never pads: a deck card is only offered as a cut when it's
     rarely played (score < 0.4) or the upgrade it makes room for clearly beats it (MIN_GAIN, same bar as the picks). */
  function cutsForFit(ctx, fit) {
    if (ctx.fmt !== 'commander' || !ctx.cutPool) return { cuts: ctx.cuts, need: ctx.cuts.length, short: 0 };
    var picks = fit.picks || [];
    var main = picks.filter(function (p) { return !p.lane; }), lanes = picks.filter(function (p) { return p.lane; });
    var needMain = Math.max(0, main.length - (ctx.openSlots || 0));
    var out = (ctx.illegalCuts || []).slice();
    var pickScores = main.map(function (p) { return p.cand.score; }).sort(function (a, b) { return b - a; });
    var used = Object.create(null), nWeak = 0, want = Math.max(0, needMain - out.length);
    for (var i = 0; i < ctx.cutPool.length && nWeak < want; i++) {
      var x = ctx.cutPool[i], k = frontKey(x.name);
      if (used[k] || picks.some(function (p) { return p.cand.key === k; })) continue;
      var base = x.base != null ? x.base : x.score;
      var ps = pickScores[want - 1 - nWeak];
      if (!(x.score < 0.4 || (ps != null && ps - Math.max(0, base) >= MIN_GAIN))) continue;
      used[k] = true; nWeak++;
      out.push({ name: x.name, why: x.why, card: x.card });
    }
    var short = Math.max(0, needMain - out.length);
    lanes.forEach(function (p) {
      var bk = frontKey(p.cut);
      out.push({ name: p.cut, why: 'swapped for ' + p.cand.name + ' (land upgrade above)', card: (ctx.deckCards && (ctx.deckCards[bk] || ctx.deckCards[nameKey(p.cut)])) || null, lane: true });
    });
    return { cuts: out, need: needMain + lanes.length, short: short, open: Math.min(ctx.openSlots || 0, main.length) };
  }

  Upgrader.prototype.render = function (fit, partial) {
    this.fit = fit;
    var ctx = this.ctx, self = this;
    this.els.results.hidden = false;
    this.els.results.classList.toggle('is-loading', !!partial);
    var fmtLabel = FORMAT_LABEL[ctx.fmt];
    var cmdrBox = this.cmdrBoxHtml();
    // Format and colors are already in the Commander block when it's shown.
    var summary = cmdrBox + this.bracketBoxHtml(fit) + (cmdrBox ? '' : '<span class="bb-db__badge bb-db__badge--sec">' + esc(fmtLabel) + '</span>' +
      '<span class="bb-db__badge bb-db__badge--sec">Colors: ' + esc(ctx.colors) + '</span>') +
      '<span class="bb-db__badge bb-db__badge--sec">' + ctx.deckCount + ' cards pasted</span>' +
      '<span class="bb-db__badge bb-db__badge--sec">Ranked by ' + esc(ctx.source) + '</span>';
    var notes = ctx.notices.slice();
    if (ctx.illegal.length) notes.push('Heads up: not legal in ' + fmtLabel + ': ' + ctx.illegal.slice(0, 6).join(', ') + (ctx.illegal.length > 6 ? '…' : '') + '.');
    if (ctx.lookupErrors) notes.push('We couldn’t check stock on ' + ctx.lookupErrors + ' card' + (ctx.lookupErrors > 1 ? 's' : '') + ' (store search was busy). Run again to retry.');
    if (ctx.fmt !== 'commander' && !partial && fit.picks.length) notes.push('We suggest 1 copy of each card. Want 2–4? Change Qty on the card.');
    var al = ctx.aliases || [];
    var aliasHtml = al.length ? '<div class="bb-du__notice bb-du__aliases" style="flex-basis:100%"><strong>Matched by alternate name:</strong><ul>' +
      al.slice(0, 12).map(function (a) { return '<li>' + esc(a.from) + ' → ' + esc(a.to) + '</li>'; }).join('') +
      (al.length > 12 ? '<li>+' + (al.length - 12) + ' more</li>' : '') + '</ul></div>' : '';
    this.els.summary.innerHTML = summary + aliasHtml + notes.map(function (n) { return '<p class="bb-du__notice" style="flex-basis:100%">' + esc(n) + '</p>'; }).join('');

    var loadingHtml = partial ? '<div class="bb-du__loading" role="status"><span class="bb-du__spinner" aria-hidden="true"></span>' + esc(this.els.status.textContent || 'Checking our stock…') + '</div>' : '';
    // One list in impact order; land swaps show their "Cut: basic → Add" line on the card itself.
    this.els.picks.innerHTML = (fit.picks.length
      ? fit.picks.map(function (p) { return cardHtml(p, 'pick', self.added[p.offer.variantId], { q: self.qtyOf(p), cap: self.qtyCap(p), inc: !self.statsOff[p.cand.key] }); }).join('')
      : (partial ? '' : '<p class="bb-db__empty">No in-stock upgrades fit your budget and filters yet. Try a bigger budget, more conditions, or “Either” for Foil.</p>')) + loadingHtml;

    var moreHtml = '';
    if (fit.more && fit.more.length && !partial) {
      var mShow = (this.moreOpen || this.roleFiltering()) ? fit.more.length : MORE_SHOWN, mRest = fit.more.length - mShow;
      moreHtml = '<div class="bb-du__more"><h3>More upgrades worth a look</h3><div class="bb-du__picks">' +
        fit.more.slice(0, mShow).map(function (p) { return cardHtml(p, 'more', self.added[p.offer.variantId]); }).join('') + '</div>' +
        (mRest > 0 ? '<div class="bb-db__actions"><button type="button" class="bb-db__btn bb-db__btn--secondary" data-bb-du-act="more-open">Show ' + mRest + ' more</button></div>' : '') + '</div>';
    }
    if (fit.outside.length) {
      this.els.outside.innerHTML = moreHtml + '<div class="bb-du__outside"><h3>More options outside your filters</h3>' +
        '<p>These strong picks are in stock, just not in the condition or foil setting you chose. Allow one and we’ll redo your picks.</p>' +
        '<div class="bb-du__picks">' + fit.outside.map(function (p) { return cardHtml(p, 'outside'); }).join('') + '</div>' +
        '<div class="bb-db__actions"><button type="button" class="bb-db__btn bb-db__btn--secondary" data-bb-du-act="include-all">Allow all of these</button></div></div>';
    } else this.els.outside.innerHTML = moreHtml;

    if (fit.held && fit.held.length && !partial) {
      var hb = fit.state.bracket;
      this.els.outside.insertAdjacentHTML('beforeend', '<div class="bb-du__held"><h3>Held back for bracket ' + hb + ' (' + esc(BRACKET_NAME[hb]) + ')</h3>' +
        '<p class="bb-db__hint">In stock and a strong fit, but adding ' + (fit.held.length === 1 ? 'it' : 'them') + ' would push your deck past your target bracket.</p><ul>' +
        fit.held.slice(0, 8).map(function (h) { return '<li><strong>' + esc(h.cand.name) + '</strong> — ' + esc(h.reason) + '</li>'; }).join('') +
        (fit.held.length > 8 ? '<li>…and ' + (fit.held.length - 8) + ' more</li>' : '') + '</ul></div>');
    }

    var cf = cutsForFit(ctx, fit), cutsShown = cf.cuts;
    this.shownCuts = cutsShown;
    var cutNote = ctx.cutNote;
    if (ctx.fmt === 'commander' && ctx.cutPool) {
      cutNote = '';
      if (cf.open) cutNote = 'Your list has ' + ctx.openSlots + ' open slot' + (ctx.openSlots === 1 ? '' : 's') + ', so ' + cf.open + ' upgrade' + (cf.open === 1 ? '' : 's') + ' need' + (cf.open === 1 ? 's' : '') + ' no cut.';
      if (cf.short) cutNote = (cutNote ? cutNote + ' ' : '') + 'The rest of your list is popular' + (ctx.edhLabel ? ' for ' + ctx.edhLabel : '') + ', so there ' + (cf.short === 1 ? 'is no clear cut for 1 upgrade' : 'are no clear cuts for ' + cf.short + ' upgrades') + '. Your call on what goes.';
    }
    if (!partial && fit.state.cuts && (cutsShown.length || cutNote)) {
      this.els.cutlist.innerHTML = '<div class="bb-du__cuts"><h3>' + (ctx.fmt === 'commander' ? 'Cards you could cut' : 'Cards to replace') + '</h3>' +
        (cutsShown.length ? '<p class="bb-db__hint">' + (ctx.fmt === 'commander' ? 'One for each upgrade above: the least-played cards in your list' + (ctx.edhLabel ? ' for ' + esc(ctx.edhLabel) : ' in Commander') + '. Your deck, your call.' : 'These aren’t legal in ' + esc(fmtLabel) + '.') + '</p><ul data-bb-du-cutcount="' + cutsShown.length + '">' +
          cutsShown.map(function (c) {
            var im = c.card ? cardImage(c.card) : null;
            return '<li>' + (im ? '<button type="button" class="bb-du__cutthumb" data-bb-du-preview="cut" data-key="' + esc(frontKey(c.name)) + '" aria-label="Preview ' + esc(c.name) + '"><img src="' + esc(im) + '" alt="" loading="lazy" width="36" height="50"></button>' : '') +
              '<span><button type="button" class="bb-du__namebtn" data-bb-du-preview="cut" data-key="' + esc(frontKey(c.name)) + '"><strong>' + esc(c.name) + '</strong></button> — ' + esc(c.why) + '</span></li>';
          }).join('') + '</ul>' : '') +
        (cutNote ? '<p class="bb-db__hint">' + esc(cutNote) + '</p>' : '') +
        (!fit.picks.some(function (p) { return p.lane; }) && !ctx.nBasics ? '<p class="bb-db__hint">Your list has no basic lands, so there are no basic-for-better-land swaps to suggest.</p>' : '') + '</div>';
    } else this.els.cutlist.innerHTML = '';

    this.renderTotals(partial);
    if (partial) { if (this.els.rolebar) this.els.rolebar.hidden = true; } else this.applyRoles();
    this.renderStats(partial);
    if (!partial) this.loadQty();
    placeStickyBar($('.bb-db__sticky-bar', this.root));
  };

  /* ---- In-page card preview (reuses the Deck Builder's lightbox styles) ---- */
  function bigImage(o, card) {
    var sf = card && (card.image_uris ? (card.image_uris.large || card.image_uris.normal) :
      (card.card_faces && card.card_faces[0] && card.card_faces[0].image_uris ? card.card_faces[0].image_uris.normal : null));
    if (o && o.image && /cdn\.shopify\.com|\/cdn\/shop\//.test(o.image)) return o.image.replace(/([?&])width=\d+/, '$1width=720');
    return sf || (o && o.image) || null;
  }
  Upgrader.prototype.buildPreview = function () {
    var self = this;
    var el = document.createElement('div');
    el.className = 'bb-db-lightbox bb-du-preview';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Card preview');
    el.hidden = true;
    el.innerHTML = '<div class="bb-db-lightbox__frame bb-du-preview__frame">' +
      '<div class="bb-du-preview__media"><button type="button" class="bb-db-lightbox__close" data-bb-du-pv-close aria-label="Close preview">×</button>' +
      '<img class="bb-db-lightbox__img bb-du-preview__img" alt=""></div>' +
      '<div class="bb-du-preview__info" data-bb-du-pv-info></div></div>';
    el.addEventListener('click', function (e) {
      if (e.target === el || e.target.closest('[data-bb-du-pv-close]')) { self.closePreview(); return; }
      var a = e.target.closest('[data-bb-du-pv-act]');
      if (!a) return;
      var act = a.getAttribute('data-bb-du-pv-act'), key = a.getAttribute('data-key');
      if (act === 'add') {
        var zp = self.pickByKey(key);
        if (zp && !self.qtyOf(zp)) self.qty[key] = 1;
        a.disabled = true; a.textContent = 'Adding…';
        self.addItems([key]).then(function () {
          var p = (self.fit ? self.fit.picks : []).filter(function (x) { return x.cand.key === key; })[0];
          var ok = p && self.added[p.offer.variantId];
          a.textContent = ok ? 'In cart ✓' : 'Add to cart'; a.disabled = !!ok;
          if (ok) a.className = 'bb-db__btn bb-db__btn--secondary';
        });
      } else if (act === 'allow') { self.closePreview(); self.includeOutside([key]); }
    });
    document.addEventListener('keydown', function (e) {
      if (el.hidden) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); self.closePreview(); }
      else if (e.key === 'Tab') {
        var f = $all('button:not([disabled]), a[href]', el);
        if (!f.length) return;
        var i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    });
    document.body.appendChild(el);
    return el;
  };
  Upgrader.prototype.openPreview = function (mode, key, opener) {
    var ctx = this.ctx, fit = this.fit;
    if (!ctx) return;
    var item = null, card = null, name = '', o = null;
    if (mode === 'pick' || mode === 'outside' || mode === 'more') {
      item = ((mode === 'pick' ? fit && fit.picks : mode === 'more' ? fit && fit.more : fit && fit.outside) || []).filter(function (p) { return p.cand.key === key; })[0];
      if (!item) return;
      o = item.offer; card = item.cand.card; name = item.cand.name;
    } else {
      var cut = (this.shownCuts || ctx.cuts).filter(function (c) { return frontKey(c.name) === key; })[0];
      if (!cut) return;
      card = cut.card; name = cut.name;
    }
    var src = bigImage(o, card);
    if (!this.preview) this.preview = this.buildPreview();
    var pv = this.preview;
    var img = $('.bb-du-preview__img', pv);
    img.removeAttribute('src');
    img.hidden = !src;
    if (img.parentNode && img.parentNode.classList) img.parentNode.classList.toggle('is-noimg', !src);
    if (src) { img.src = src; img.alt = name; }
    var html = '<h3 class="bb-du-preview__name">' + esc(name) + '</h3>';
    if (o) {
      var added = mode === 'pick' && this.added[o.variantId];
      html += '<p class="bb-du-preview__meta">' + esc([o.setName + (o.setCode ? ' (' + o.setCode + (o.collector ? ' #' + o.collector : '') + ')' : ''), COND_LABEL[o.cond], o.foil ? 'Foil' : 'Nonfoil', o.treatment].filter(Boolean).join(' · ')) + '</p>' +
        (item.cand.why ? '<p class="bb-du-preview__why">' + esc(item.cand.why) + '</p>' : '') +
        '<div class="bb-du-preview__row"><span class="bb-du-preview__price">' + money(o.price) + '</span> <span class="bb-du__qty" data-bb-du-pv-qty></span></div>' +
        '<div class="bb-du-preview__actions">' +
        (mode === 'pick'
          ? '<button type="button" class="bb-db__btn ' + (added ? 'bb-db__btn--secondary' : 'bb-db__btn--primary') + '" data-bb-du-pv-act="add" data-key="' + esc(key) + '"' + (added ? ' disabled' : '') + '>' + (added ? 'In cart ✓' : 'Add to cart') + '</button>'
          : '<button type="button" class="bb-db__btn bb-db__btn--primary" data-bb-du-pv-act="allow" data-key="' + esc(key) + '">Allow ' + esc(item.reason) + '</button>') +
        '<a class="bb-du-preview__link" href="' + esc(o.url) + '" target="_blank" rel="noopener">View product page ↗</a></div>';
    } else {
      html += '<p class="bb-du-preview__meta">In your deck' + (card && card.type_line ? ' · ' + esc(card.type_line) : '') + '</p>' +
        '<div class="bb-du-preview__actions"><a class="bb-du-preview__link" href="' + rootPath() + 'search?q=' + encodeURIComponent(name) + '&type=product" target="_blank" rel="noopener">See our copies ↗</a></div>';
    }
    $('[data-bb-du-pv-info]', pv).innerHTML = html;
    if (o) {
      var q = $('[data-bb-du-pv-qty]', pv);
      stockQty(o.handle).then(function (map) { q.textContent = qtyLabel(map[o.variantId]); q.classList.toggle('is-low', map[o.variantId] === 1); });
    }
    pv.__opener = opener || document.activeElement;
    pv.hidden = false;
    document.documentElement.classList.add('bb-db-lightbox-open');
    $('[data-bb-du-pv-close]', pv).focus();
  };
  Upgrader.prototype.closePreview = function () {
    var pv = this.preview;
    if (!pv || pv.hidden) return;
    pv.hidden = true;
    document.documentElement.classList.remove('bb-db-lightbox-open');
    var o = pv.__opener; pv.__opener = null;
    if (o && o.focus && document.contains(o)) o.focus();
  };

  function bracketLabel(est) { return est >= 4 ? '4–5 (Optimized / cEDH)' : est === 3 ? '3 (Upgraded)' : '1–2 (Exhibition / Core)'; }
  function bracketShort(est) { return est >= 4 ? '4–5' : est === 3 ? '3' : '1–2'; }
  /* "Current bracket: 3 (Upgraded)" + the short reason; shown in the Commander block. */
  function currentBracketHtml(a) {
    if (!a) return '';
    return '<p class="bb-du__cmdrbracket"><span class="bb-du__cmdrbracket-label">Current bracket:</span> <strong class="bb-du__bracketest">' + esc(bracketLabel(a.est)) + '</strong>' +
      (a.reasons.length ? '<span class="bb-du__sub bb-du__cmdrbracket-why">because of ' + esc(a.reasons.join(', ')) + '</span>' : '') + '</p>' + currentStatsHtml(a);
  }

  /* The current deck's bracket stats (Game Changers, combos, ...); explain the current bracket, so they sit under it. */
  function currentStatsHtml(a) {
    function list(arr, n) { return arr.slice(0, n).map(esc).join(', ') + (arr.length > n ? ', +' + (arr.length - n) + ' more' : ''); }
    var rows = [];
    rows.push('<li><strong>Game Changers: ' + a.gc.length + '</strong>' + (a.gc.length ? ' — ' + list(a.gc, 12) : '') + '</li>');
    if (a.combos.length) {
      rows.push('<li><strong>Two-card combos: ' + a.combos.length + (a.early.length ? ' (' + a.early.length + ' early)' : '') + '</strong><ul>' +
        a.combos.slice(0, 6).map(function (x) {
          return '<li>' + esc(x.a) + ' + ' + esc(x.b) + ' <span class="bb-du__sub">' + x.mv + ' mana · ' + esc(x.result) + '</span>' +
            (x.early ? ' <span class="bb-du__tag bb-du__tag--warn" title="Both pieces cost under ' + EARLY_COMBO_MV + ' mana total">Early</span>' : '') + '</li>';
        }).join('') + (a.combos.length > 6 ? '<li>…and ' + (a.combos.length - 6) + ' more</li>' : '') + '</ul></li>');
    } else rows.push('<li><strong>Two-card combos:</strong> none found</li>');
    if (a.mld.length) rows.push('<li><strong>Mass land denial:</strong> ' + list(a.mld, 6) + '</li>');
    if (a.xt.length) rows.push('<li><strong>Extra-turn cards: ' + a.xt.length + '</strong> — ' + list(a.xt, 6) + '</li>');
    return '<ul class="bb-du__bracketlist bb-du__cmdrstats">' + rows.join('') + '</ul>';
  }

  Upgrader.prototype.cmdrBoxHtml = function () {
    var ctx = this.ctx;
    if (!ctx || ctx.fmt !== 'commander' || !ctx.commanders.length) return '';
    var info = ctx.cmdrInfo;
    var current = ctx.commanders.map(function (c) { return c.name; }).join(' + ');
    var how = info ? info.how : 'typed';
    var msg = {
      header: 'Found in your list’s Commander section.',
      bottom: 'Found at the bottom of your list (Moxfield-style).',
      top: 'Found at the top of your list.',
      picked: 'You picked this one.',
      guess: 'Your list doesn’t mark a commander, so this is our best guess. Wrong? Pick yours below.',
      typed: 'From the Commander box above.'
    }[how] || '';
    var opts = [];
    if (info) {
      opts.push(current);
      info.candidates.forEach(function (c) { if (opts.indexOf(c.name) === -1) opts.push(c.name); });
      var partners = info.candidates.filter(canPartner);
      for (var i = 0; i < partners.length; i++) for (var j = i + 1; j < partners.length; j++) {
        var pr = partners[i].name + ' + ' + partners[j].name;
        if (opts.indexOf(pr) === -1 && opts.indexOf(partners[j].name + ' + ' + partners[i].name) === -1) opts.push(pr);
      }
    }
    var select = opts.length > 1
      ? '<label class="bb-du__cmdrpick"><span>Change commander:</span> <select data-bb-du-cmdr-pick aria-label="Change commander">' +
        opts.map(function (n) { return '<option value="' + esc(n) + '"' + (n === current ? ' selected' : '') + '>' + esc(n) + '</option>'; }).join('') + '</select></label>'
      : '';
    return '<div class="bb-du__cmdrbox' + (how === 'guess' ? ' is-guess' : '') + '" style="flex-basis:100%">' +
      '<div><span class="bb-db__control-title">' + (how === 'guess' ? 'Commander (guessed)' : 'Commander') + '</span>' +
      '<strong class="bb-du__cmdrname">' + esc(current) + '</strong> <span class="bb-du__sub">Colors: ' + esc(ctx.colors) + '</span>' +
      '<p class="bb-du__cmdrmsg">' + esc(msg) + '</p>' + currentBracketHtml(ctx.analysis) + '</div>' + select + '</div>';
  };

  Upgrader.prototype.bracketBoxHtml = function (fit) {
    var ctx = this.ctx, a = ctx && ctx.analysis;
    if (!a) return '';
    var sel = fit && fit.state ? fit.state.bracket : 0;
    var over = sel ? a.over(sel) : [];
    var warn = over.length
      ? '<p class="bb-du__bracketwarn" role="alert">Your deck is already above bracket ' + sel + ' (' + esc(BRACKET_NAME[sel]) + '): ' + esc(over.join('; ')) + '. We won’t suggest anything that adds to it.</p>'
      : '';
    var push = sel && pushMode(sel, a);
    // Leads with "Current → Target"; the current estimate itself lives in the Commander block.
    var target = '<p class="bb-du__brackettarget"><strong>Current: bracket ' + bracketShort(a.est) + ' → Target: ' +
      (sel ? 'bracket ' + sel + ' (' + esc(BRACKET_NAME[sel]) + ')' : 'any bracket (no target set)') + '</strong></p>';
    var p = fit && fit.projected;
    if (p && fit.picks && fit.picks.length) {
      target += '<p class="bb-du__bracketproj">With ' + (fit.picks.length === 1 ? 'this pick' : 'these ' + fit.picks.length + ' picks') + ' added: <strong>bracket ' + bracketShort(p.est) + '</strong> <span class="bb-du__sub">(' +
        p.gc.length + ' Game Changer' + (p.gc.length === 1 ? '' : 's') + ', ' + p.combos.length + ' two-card combo' + (p.combos.length === 1 ? '' : 's') +
        (p.early.length ? ', ' + p.early.length + ' early' : '') + ')</span></p>';
    }
    var selTxt;
    if (!sel) selTxt = 'Pick a target bracket above and we’ll steer the upgrades toward it.';
    else if (push) {
      selTxt = sel === 3 ? 'Building up to bracket 3: we rank Game Changers higher (up to 3 in total) and still skip early two-card combos.'
        : sel === 4 ? 'Building toward bracket 4: we rank Game Changers, fast mana, efficient tutors, free and cheap interaction and cards that complete combos with your deck higher.'
        : 'Building toward bracket 5: we rank like a cEDH deck — fast mana, free and cheap interaction, efficient tutors, Game Changers and compact combos with your deck come first.';
    } else selTxt = 'Your deck is already at or above this bracket, so upgrades stay within bracket ' + sel + ' (' + BRACKET_NAME[sel] + ').';
    var b45 = sel >= 4 ? ' Brackets 4 and 5 share the same card rules; bracket 5 just leans harder into cEDH staples.' : '';
    var earlyShown = a.early.length || (p && fit.picks && fit.picks.length && p.early.length);
    var src = (earlyShown ? ' Early combo = both pieces cost under ' + EARLY_COMBO_MV + ' mana total.' : '') + ' Data: Scryfall and Commander Spellbook.';
    return '<div class="bb-du__bracketbox' + (over.length ? ' is-over' : '') + '" style="flex-basis:100%">' +
      '<div class="bb-du__brackethead"><span class="bb-db__control-title">Bracket target</span>' +
      // Fallback: no Commander block was drawn, so show the estimate here instead.
      (this.cmdrBoxHtml() ? '' : currentBracketHtml(a)) + '</div>' +
      target + warn +
      '<p class="bb-du__sub">' + esc(selTxt) + esc(b45) + esc(src) + '</p></div>';
  };

  /* bb-du qty0 v1: Qty per pick. 0 = "I already own it": the card stays (dimmed), is left out of Add all and the total. */
  /* ---- bb-du roles v1 ---- */
  Upgrader.prototype.roleFiltering = function () { return Object.keys(this.roleOn).length > 0; };
  Upgrader.prototype.bindRoles = function () {
    var self = this, bar = this.els.rolebar;
    if (!bar) return;
    bar.addEventListener('click', function (e) {
      var b = e.target.closest('[data-bb-du-role]');
      if (!b) return;
      var r = b.getAttribute('data-bb-du-role');
      var was = self.roleFiltering();
      if (!r) self.roleOn = Object.create(null);
      else if (self.roleOn[r]) delete self.roleOn[r];
      else self.roleOn[r] = true;
      // "More upgrades" shows every match while filtering, so its list has to be redrawn when filtering starts/stops.
      if (was !== self.roleFiltering() && self.fit && self.fit.more && self.fit.more.length > MORE_SHOWN && !self.moreOpen) self.render(self.fit, false);
      else self.applyRoles();
      var nb = bar.querySelector('[data-bb-du-role="' + r + '"]') || bar.querySelector('[data-bb-du-role=""]');
      if (nb) nb.focus();
    });
    bar.addEventListener('change', function (e) {
      if (!e.target.matches('[data-bb-du-sort]')) return;
      self.sortBy = e.target.value;
      self.applyRoles();
      var s = bar.querySelector('[data-bb-du-sort]'); if (s) s.focus();
    });
  };
  Upgrader.prototype.applyRoles = function () {
    var self = this, fit = this.fit, bar = this.els.rolebar;
    if (!bar || !fit) return;
    var all = fit.picks.concat(fit.more || []);
    if (!fit.picks.length) { bar.hidden = true; bar.innerHTML = ''; return; }
    var byKey = Object.create(null), counts = Object.create(null);
    all.forEach(function (p) {
      var rs = rolesOf(p.cand, self.roleIdx);
      byKey[p.cand.key] = { p: p, roles: rs };
      Object.keys(rs).forEach(function (r) { counts[r] = (counts[r] || 0) + 1; });
    });
    Object.keys(this.roleOn).forEach(function (r) { if (!counts[r]) delete self.roleOn[r]; });
    var on = Object.keys(this.roleOn), filtering = on.length > 0;
    function match(key) { var x = byKey[key]; return !filtering || !!(x && on.some(function (r) { return x.roles[r]; })); }
    function rank(key) { var x = byKey[key]; if (!x) return 99; for (var i = 0; i < ROLE_CHIPS.length; i++) if (x.roles[ROLE_CHIPS[i][0]]) return i; return 98; }
    // Show / hide + sort each list (picks, More upgrades). Original order = best first.
    var shownPicks = 0, shownMore = 0;
    [$('[data-bb-du-picks]', this.root), $('.bb-du__more .bb-du__picks', this.root)].forEach(function (grid, gi) {
      if (!grid) return;
      var cards = $all(':scope > .bb-du__card', grid);
      cards.forEach(function (el, i) { if (el.__bbOrd == null) el.__bbOrd = i; });
      var vis = 0;
      cards.forEach(function (el) { var ok = match(el.getAttribute('data-key')); el.hidden = !ok; if (ok) vis++; });
      if (gi === 0) shownPicks = vis; else shownMore = vis;
      var price = function (el) { var x = byKey[el.getAttribute('data-key')]; return x ? x.p.offer.price : 0; };
      var cmp = self.sortBy === 'price-asc' ? function (a, b) { return price(a) - price(b) || a.__bbOrd - b.__bbOrd; }
        : self.sortBy === 'price-desc' ? function (a, b) { return price(b) - price(a) || a.__bbOrd - b.__bbOrd; }
        : self.sortBy === 'role' ? function (a, b) { return rank(a.getAttribute('data-key')) - rank(b.getAttribute('data-key')) || a.__bbOrd - b.__bbOrd; }
        : function (a, b) { return a.__bbOrd - b.__bbOrd; };
      var tail = $all(':scope > :not(.bb-du__card)', grid);
      cards.slice().sort(cmp).forEach(function (el) { grid.appendChild(el); });
      tail.forEach(function (el) { grid.appendChild(el); });
      if (gi === 1) {
        var box = grid.closest('.bb-du__more');
        if (box) box.hidden = filtering && !vis;
      }
    });
    var empty = $('[data-bb-du-picks] [data-bb-du-roleempty]', this.root);
    if (filtering && !shownPicks) {
      if (!empty) { empty = document.createElement('p'); empty.className = 'bb-db__empty'; empty.setAttribute('data-bb-du-roleempty', ''); this.els.picks.appendChild(empty); }
      empty.textContent = 'None of your picks match. Matching cards are under “More upgrades worth a look” below.';
    } else if (empty) empty.remove();
    var chips = '<button type="button" class="bb-db__chip' + (filtering ? '' : ' is-on') + '" data-bb-du-role="" aria-pressed="' + !filtering + '">All <span class="bb-du__rolen">' + all.length + '</span></button>' +
      ROLE_CHIPS.filter(function (r) { return counts[r[0]]; }).map(function (r) {
        var isOn = !!self.roleOn[r[0]];
        return '<button type="button" class="bb-db__chip' + (isOn ? ' is-on' : '') + '" data-bb-du-role="' + r[0] + '" aria-pressed="' + isOn + '">' + esc(r[1]) + ' <span class="bb-du__rolen">' + counts[r[0]] + '</span></button>';
      }).join('');
    var opts = [['best', 'Best first'], ['price-asc', 'Price: low to high'], ['price-desc', 'Price: high to low'], ['role', 'Role']];
    bar.innerHTML = '<div class="bb-db__chips bb-du__rolechips" role="group" aria-label="Show upgrades by role">' + chips + '</div>' +
      '<label class="bb-db__line-meta bb-du__sort">Sort <select data-bb-du-sort aria-label="Sort upgrades">' +
      opts.map(function (o) { return '<option value="' + o[0] + '"' + (self.sortBy === o[0] ? ' selected' : '') + '>' + o[1] + '</option>'; }).join('') + '</select></label>' +
      (filtering ? '<span class="bb-du__roleshown">Showing ' + shownPicks + ' of ' + fit.picks.length + ' picks' + (shownMore ? ' + ' + shownMore + ' under More upgrades' : '') + '. Add all still adds every pick set to 1+.</span>' : '');
    bar.hidden = false;
  };

  /* ---- bb-du stats v1: live deck stats (math in assets/bb-deck-stats.js; this part builds the before/after decks) ---- */
  // Fast mana = free or mana-positive acceleration (not every 2-mana rock).
  var FAST_CORE = keySet(['Sol Ring', 'Mana Crypt', 'Mana Vault', 'Grim Monolith', 'Basalt Monolith', 'Chrome Mox', 'Mox Diamond', 'Mox Opal', 'Mox Amber', 'Mox Tantalite',
    'Mox Pearl', 'Mox Sapphire', 'Mox Jet', 'Mox Ruby', 'Mox Emerald', 'Lotus Petal', 'Jeweled Lotus', "Lion's Eye Diamond", 'Lotus Bloom', 'Ancient Tomb', 'City of Traitors',
    "Mishra's Workshop", 'Dark Ritual', 'Cabal Ritual', 'Pyretic Ritual', 'Desperate Ritual', 'Rite of Flame', 'Seething Song', 'Simian Spirit Guide', 'Elvish Spirit Guide',
    'Culling the Weak', 'Springleaf Drum', 'Mana Geyser', 'Energy Vault']);
  function statFlags(ctx, c, idx) {
    var key = frontKey(c.name), isLand = isLandCard(c);
    var gc = ctx.fmt === 'commander' ? (ctx.bd ? isGameChanger(ctx.bd, key, c) : !!c.game_changer) : false;
    var r = rolesOf({ key: key, card: c, role: cardRole(c, ctx.colors), upTags: [], isGC: gc }, idx);
    return { ramp: !!r.ramp && !isLand, draw: !!r.draw, removal: !!r.removal, wipe: !!r.wipe, counter: !!r.counter,
      tutor: (!!r.tutor && !r.ramp && !isLand) || !!TUTOR_EXTRA[key], fast: !!FAST_CORE[key], gc: gc };
  }
  Upgrader.prototype.statsItems = function (extraPicks, cutList) {
    var ctx = this.ctx, self = this, cmdrKeys = ctx.cmdrKeys || {};
    var items = [], byKey = Object.create(null);
    var inCmdr = Object.create(null);
    (ctx.commanders || []).forEach(function (c) { inCmdr[frontKey(c.name)] = true; });
    (ctx.deckList || []).forEach(function (e) {
      var k = e.card ? frontKey(e.card.name) : frontKey(e.name);
      if (ctx.fmt === 'commander' && (inCmdr[k] || cmdrKeys[k])) return; // the commander sits in the command zone
      var it = { key: k, name: e.card ? e.card.name : e.name, qty: e.qty, card: e.card };
      items.push(it); byKey[k] = byKey[k] || it;
    });
    var removed = 0;
    (cutList || []).forEach(function (cut) {
      var it = byKey[frontKey(cut.name)] || byKey[nameKey(cut.name)];
      if (!it || it.qty <= 0) return;
      var n = cut.all ? it.qty : (cut.n || 1);
      n = Math.min(n, it.qty); it.qty -= n; removed += n;
    });
    (extraPicks || []).forEach(function (p) {
      var q = ctx.fmt === 'commander' ? 1 : Math.max(1, self.qtyOf(p));
      var ex = byKey[p.cand.key];
      if (ex) ex.qty += q; else items.push({ key: p.cand.key, name: p.cand.name, qty: q, card: p.cand.card });
    });
    return { items: items, removed: removed };
  };
  Upgrader.prototype.renderStats = function (partial) {
    var host = this.els.stats, ctx = this.ctx, fit = this.fit, self = this;
    if (!host) return;
    var S = window.BBDeckStats;
    if (!S || !ctx || !fit || partial || !(ctx.deckList || []).length) { host.hidden = true; return; }
    try {
      var fmt = ctx.fmt, idx = this.roleIdx;
      var picks = fit.picks.filter(function (p) { return !self.statsOff[p.cand.key]; });
      var flagCache = Object.create(null);
      function addFlags(list) {
        list.forEach(function (it) {
          if (!it.card) return;
          var k = it.key; if (!flagCache[k]) flagCache[k] = statFlags(ctx, it.card, idx);
          it.f = flagCache[k];
        });
        return list;
      }
      var cuts = [], short = 0;
      if (picks.length) {
        if (fmt === 'commander' && ctx.cutPool) {
          var cf = cutsForFit(ctx, { picks: picks });
          cuts = cf.cuts.map(function (c) { return { name: c.name }; }); short = cf.short || 0;
        } else {
          cuts = (ctx.illegalCuts || []).map(function (c) { return { name: c.name, all: true }; });
          picks.forEach(function (p) { if (p.lane && p.cut) cuts.push({ name: p.cut, n: Math.max(1, self.qtyOf(p)) }); });
        }
      }
      var base = addFlags(this.statsItems([], []).items);
      var aft = this.statsItems(picks, cuts);
      addFlags(aft.items);
      var colorStr = ctx.colors === 'C' ? '' : ctx.colors;
      var o = { fmt: fmt, colors: colorStr, commanders: fmt === 'commander' ? ctx.commanders : [], drawFirst: fmt === 'commander' };
      var A = S.analyze(base, o), B = S.analyze(aft.items, o);
      if (ctx.bd && fmt === 'commander') {
        [[A, base], [B, aft.items]].forEach(function (pr) {
          var m = Object.create(null);
          pr[1].forEach(function (it) { if (it.card && it.qty > 0) m[frontKey(it.card.name)] = it.card; });
          (ctx.commanders || []).forEach(function (c) { m[frontKey(c.name)] = c; });
          var a = analyzeDeck(ctx.bd, m, ctx.cmdrKeys);
          pr[0].edh = { xt: a.xt.length, combos: a.combos.length, mld: a.mld.length, est: a.est };
        });
      }
      var libN = A.n, libNote = '';
      if (fmt === 'commander' && libN !== 99) libNote = 'Your list has ' + libN + ' cards besides the commander, so the odds use a ' + libN + '-card library.';
      var info = { fmt: fmt, hasChange: picks.length > 0, picks: picks.length, cuts: aft.removed, short: picks.length ? short : 0, libNote: libNote,
        colors: ctx.colors === 'C' ? [] : ctx.colors.split(''), noPicks: !fit.picks.length };
      var head = S.fill($('[data-bb-ds-body]', host), A, B, info);
      var hd = $('[data-bb-ds-head]', host); if (hd) hd.textContent = head;
      host.hidden = false;
    } catch (err) {
      console.error('[bb-deck-upgrade stats]', err);
      host.hidden = true;
    }
  };
  Upgrader.prototype.pickByKey = function (key) {
    return ((this.fit && this.fit.picks) || []).filter(function (p) { return p.cand.key === key; })[0] || null;
  };
  Upgrader.prototype.qtyCap = function (p) {
    var cap = this.ctx && this.ctx.fmt === 'commander' ? 1 : 4; // Commander is singleton; other formats max 4 copies
    var s = this.stock[p.offer.variantId];
    if (typeof s === 'number' && s >= 1) cap = Math.min(cap, s); // never more than we have in stock
    return cap;
  };
  Upgrader.prototype.qtyOf = function (p) {
    var q = this.qty[p.cand.key];
    return Math.max(0, Math.min(q == null ? 1 : q, this.qtyCap(p)));
  };
  Upgrader.prototype.syncQtyCard = function (p) {
    var card = $all('[data-bb-du-picks] .bb-du__card', this.root).filter(function (el) { return el.getAttribute('data-key') === p.cand.key; })[0];
    if (!card) return;
    var q = this.qtyOf(p), added = !!this.added[p.offer.variantId];
    card.classList.toggle('is-zero', !added && q === 0);
    var inp = $('[data-bb-du-setqty]', card);
    if (inp) { inp.max = String(this.qtyCap(p)); if (document.activeElement !== inp || parseInt(inp.value, 10) > q) inp.value = String(q); }
    var add = $('[data-bb-du-act="add"]', card);
    if (add && !added) add.disabled = q === 0;
  };
  Upgrader.prototype.renderTotals = function (partial) {
    var fit = this.fit, self = this;
    if (!fit) return;
    var addedCents = 0, pendingCents = 0, pendingN = 0, keptPicks = 0;
    fit.picks.forEach(function (p) {
      if (self.added[p.offer.variantId]) { addedCents += p.offer.price * (self.addedQty[p.offer.variantId] || 1); keptPicks++; return; }
      var q = self.qtyOf(p);
      if (q > 0) { pendingCents += p.offer.price * q; pendingN += q; keptPicks++; }
    });
    var chosen = addedCents + pendingCents, zeroN = fit.picks.length - keptPicks;
    this.els.total.textContent = money(chosen) + ' of ' + money(fit.budget);
    if (this.els.totalSub) {
      var pk = fit.picks.length;
      this.els.totalSub.textContent = addedCents ? 'In cart ' + money(addedCents) + (pendingCents ? ' · Not added yet ' + money(pendingCents) : (zeroN ? '' : ' · all picks added')) :
        (pk ? (zeroN ? keptPicks + ' of ' + pk : pk) + ' pick' + (pk === 1 ? '' : 's') + ' · ' + money(Math.max(0, fit.budget - chosen)) + ' of budget left' : '');
    }
    this.els.meter.style.width = (fit.budget ? Math.min(100, Math.round((chosen / fit.budget) * 100)) : 0) + '%';
    if (this.els.meterAdded) this.els.meterAdded.style.width = (fit.budget ? Math.min(100, Math.round((addedCents / fit.budget) * 100)) : 0) + '%';
    this.els.addAll.disabled = !pendingN || partial;
    this.els.addAll.textContent = pendingN ? (addedCents ? 'Add remaining ' + pendingN + ' to cart' : (zeroN ? 'Add ' + pendingN + ' to cart' : 'Add all ' + pendingN + ' to cart')) : (fit.picks.length && addedCents ? 'All added ✓' : 'Add all to cart');
    if (this.els.viewCart) this.els.viewCart.hidden = !Object.keys(this.added).length;
  };

  Upgrader.prototype.loadQty = function () {
    var self = this;
    $all('[data-bb-du-qty]', this.root).forEach(function (el) {
      var id = el.getAttribute('data-bb-du-qty');
      stockQty(el.getAttribute('data-handle')).then(function (map) {
        var label = qtyLabel(map[id]);
        el.textContent = label;
        el.classList.toggle('is-low', map[id] === 1);
        if (typeof map[id] === 'number' && self.stock[id] !== map[id]) {
          self.stock[id] = map[id];
          var p = ((self.fit && self.fit.picks) || []).filter(function (x) { return String(x.offer.variantId) === String(id); })[0];
          if (p) { self.syncQtyCard(p); self.renderTotals(false); }
        }
      });
    });
  };

  Upgrader.prototype.addItems = async function (keys, btn) {
    var self = this;
    var pickSet = (this.fit ? this.fit.picks : []);
    function want(p) { return pickSet.indexOf(p) !== -1 ? self.qtyOf(p) : 1; } // Qty 0 = already owned: never added
    var picks = (this.fit ? this.fit.picks.concat(this.fit.more || []) : []).filter(function (p) { return keys.indexOf(p.cand.key) !== -1 && !self.added[p.offer.variantId] && want(p) > 0; });
    if (!picks.length) return;
    var total = picks.length, total1 = picks[0].cand.name;
    if (btn) { btn.disabled = true; btn.classList.add('is-busy'); btn.textContent = 'Adding…'; }
    this.setStatus('Adding ' + picks.length + ' card' + (picks.length > 1 ? 's' : '') + ' to your cart…', 'busy');
    var notes = [], addedN = 0, already = [];
    var st = this.state();
    try {
      // 1) Anything already in the cart (same printing, or any copy of that product) just counts as "In cart".
      var inCart = await cartVariantQty();
      picks = picks.filter(function (p) {
        if (inCart.byVariant[p.offer.variantId] || inCart.byHandle[p.offer.handle]) { self.added[p.offer.variantId] = true; already.push(p.cand.name); return false; }
        return true;
      });
      // 2) Add the rest in one request; if Shopify rejects the batch, go one by one so one problem can't block the others.
      if (picks.length) {
        var r = await postCartAdd(picks.map(function (p) { return { id: p.offer.variantId, quantity: want(p) }; }));
        if (r.ok) { picks.forEach(function (p) { self.added[p.offer.variantId] = true; self.addedQty[p.offer.variantId] = want(p); addedN += want(p); }); }
        else {
          for (var i = 0; i < picks.length; i++) {
            var p = picks[i];
            var one = await postCartAdd([{ id: p.offer.variantId, quantity: want(p) }]);
            if (one.ok) { self.added[p.offer.variantId] = true; self.addedQty[p.offer.variantId] = want(p); addedN += want(p); continue; }
            var kind = cartErrorKind(one);
            if (kind === 'max') { self.added[p.offer.variantId] = true; already.push(p.cand.name); continue; }
            // 3) That copy can't be added: try another in-stock printing/condition of the same card within the filters.
            var swapped = await self.trySwap(p, st);
            if (swapped) { addedN++; notes.push(p.cand.name + ': that copy just sold, so we added ' + offerLabel(swapped) + ' instead (' + money(swapped.price) + ').'); continue; }
            notes.push(p.cand.name + ': ' + (kind === 'soldout' ? 'just sold out, and we have no other copy in your filters.' : (one.message || 'the store couldn’t add it') + '.'));
          }
        }
      }
      var cart = await refreshCartUi();
      this.render(this.fit, false);
      var tail = cart ? ' Your cart has ' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's') + '.' : '';
      var msg = [];
      if (addedN) msg.push('Added ' + (addedN === 1 && total === 1 ? total1 : addedN + ' card' + (addedN === 1 ? '' : 's')) + ' to your cart.');
      if (already.length) msg.push('Already in your cart: ' + already.join(', ') + '.');
      msg = msg.concat(notes);
      this.setStatus(msg.join(' ') + tail, notes.length ? 'error' : 'ok');
    } catch (e) {
      if (btn) { btn.disabled = false; btn.classList.remove('is-busy'); btn.textContent = 'Add to cart'; }
      this.setStatus('Couldn’t reach the cart. Please try again.', 'error');
    }
  };

  /* Swap a pick to the next cheapest in-stock copy of the same card that fits the filters (and isn't over budget). */
  Upgrader.prototype.trySwap = async function (p, st) {
    var self = this, fit = this.fit;
    function finishOk(o) { return st.finish === 'either' || (st.finish === 'foil' ? o.foil : !o.foil); }
    var room = fit.budget - fit.spent + p.offer.price;
    var tried = {}; tried[p.offer.variantId] = 1;
    var alts = (p.cand.offers || []).filter(function (o) { return !tried[o.variantId] && st.conds.indexOf(o.cond) !== -1 && finishOk(o) && o.price <= room; });
    for (var i = 0; i < alts.length && i < 3; i++) {
      var r = await postCartAdd([{ id: alts[i].variantId, quantity: 1 }]);
      if (r.ok) {
        p.cand.offers = p.cand.offers.filter(function (o) { return o.variantId !== p.offer.variantId; });
        fit.spent += alts[i].price - p.offer.price;
        p.offer = alts[i];
        self.added[alts[i].variantId] = true;
        return alts[i];
      }
      if (cartErrorKind(r) === 'max') { p.offer = alts[i]; self.added[alts[i].variantId] = true; return alts[i]; }
    }
    return null;
  };

  function init() {
    $all(ROOT_SEL).forEach(function (root) {
      if (root.__bbDu) return;
      root.__bbDu = new Upgrader(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', init);
  window.BBDeckUpgrade = { parseDeck: parseDeck, cardNameFromTitle: cardNameFromTitle, conditionId: conditionId, init: init };
})();
